import { compareByOceanStatusOrder } from './ocean-status-order.mjs?v=20260806.100';
import {
  authorizedColumns,
  buildColumnView,
  mountWorkspaceTier,
  workspaceTier,
} from './adaptive-workspace.mjs?v=20260806.100';
import {
  createDashboardColumnPinStore,
  dashboardPinnedColumnOffsets,
  orderDashboardColumns,
} from './dashboard-column-pinning.mjs?v=20260806.100';
import {
  columnEditableForRole,
  columnWidthsForLocale,
  fieldOptions,
  rowUsesCustomerOwnChassis,
  containerNumberEditable,
} from './container-field-registry.mjs?v=20260806.100';
import {
  CUSTOM_ADDRESS_VALUE,
  customerMatchKey,
  isCustomAddress,
  normalizeAddressText,
} from './address-catalog.mjs?v=20260806.100';
import { containerStatusLabel, containerStatusPresentation } from './container-status-presentation.mjs?v=20260806.100';
import { createOverlayManager } from './dashboard-overlay-manager.mjs?v=20260806.100';
import {
  dashboardCellContent,
  dashboardEditorInputSpec,
  dashboardEditorValue,
} from './dashboard-cell-interactions.mjs?v=20260806.100';
import {
  dashboardDriverOptionsMarkup,
  dashboardDriverTypedMarkup,
  normalizeDashboardDrivers,
  typedDriverName,
} from './dashboard-driver-dropdown.mjs?v=20260806.100';
import { poundsFromWeightInput } from './weight-units.mjs?v=20260806.100';
import { parseReportedEmptyEntry } from './reported-empty-entry.mjs?v=20260806.100';
import { TYPED_DATE_TIME_FIELDS, isLocalMidnight } from './typed-date.mjs?v=20260806.100';
import { APPEND_ONLY_NOTE_FIELDS } from './dashboard-field-autosave.mjs?v=20260806.100';
import {
  containerNumberOf,
  createEmptyContainerPickMode,
  emptyPickBannerMarkup,
  externalEmptyPickBannerMarkup,
  reportedEmptyCandidates,
} from './empty-container-picker.mjs?v=20260806.100';

// Same rows, in the same order, at the same revision. Compared by identity
// first, because the controller reuses row objects between reports and that
// settles it in one pass; the id/revision pair is the fallback for a set that
// was rebuilt but says the same thing. A row with no revision is treated as
// changed, so an unknown is never mistaken for "nothing happened".
function rowsAreIdentical(current, next) {
  if (!Array.isArray(current) || !Array.isArray(next)) return false;
  if (current.length !== next.length) return false;
  for (let index = 0; index < current.length; index += 1) {
    const before = current[index];
    const after = next[index];
    if (before === after) continue;
    if (!before || !after) return false;
    if (before.id !== after.id) return false;
    if (!Number.isFinite(before.revision) || !Number.isFinite(after.revision)) return false;
    if (before.revision !== after.revision) return false;
  }
  return true;
}

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function optionSlug(value) {
  return String(value || 'neutral').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'neutral';
}

// "Customer Paid" is the third answer for a fee, and it has to sit in a menu beside
// two one-word options without stretching the column - so it reads "C Paid"
// (Alan, 2026-08-02).
const OPTION_LABEL_OVERRIDES = Object.freeze({ customer_paid: 'C Paid' });

// The Ocean Status menu reads in the same words as the column it writes to
// (Alan, 2026-08-09): 已报空, 已送仓, 转仓, 可提, 查验, pending, 未到港, 已还空, 取消.
// Every other menu in the table - fees, sizes, pre-alert - keeps the humanised
// token, because those are not statuses and have no Chinese name of their own.
// Customer Chassis stores the inverse of what it displays: "yes" means we
// supplied the chassis, so the customer had none. Naming the two options after
// the fact rather than after the letter is the only way the menu and the cell
// beside it can agree (Alan, 2026-08-09).
// Yes means own chassis, no means not (Alan, 2026-08-10). The token and the label
// finally agree, so the menu can say Yes and No without meaning the opposite of
// the cell beside it.
const CUSTOMER_CHASSIS_OPTION_LABELS = Object.freeze({
  yes: 'Yes 客户自带',
  no: 'No 我们提供',
});

function optionLabel(value, column = null) {
  const key = String(value || '');
  if (column?.key === 'customer_chassis') return CUSTOMER_CHASSIS_OPTION_LABELS[key] || key;
  if (column?.key === 'ocean_status') return containerStatusLabel(key, 'zh-CN');
  // An open menu's options are values somebody typed, not tokens this app coined,
  // so they are drawn exactly as they are stored. Title Casing turns the carrier
  // COSCO into "Cosco" and the terminal PCT into "Pct" - names nobody uses.
  if (column?.allowCustom) return key;
  return OPTION_LABEL_OVERRIDES[key]
    ?? key.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// Every Ocean Status a Container can hold needs a tone. The four that were missing here account
// for 299 of the 410 production Containers — 73% of the table fell through to grey, which is why
// the colour system looked like it was not there at all (Alan, 2026-07-31).
const CONTAINER_STATUS_TONES = Object.freeze({
  delivered_to_warehouse: 'delivered-to-warehouse',
  arrived: 'arrived-at-port',
  arrived_at_port: 'arrived-at-port',
  available: 'available-for-pickup',
  available_for_pickup: 'available-for-pickup',
  pending: 'pending',
  cancelled: 'cancelled',
  not_arrived: 'not-arrived',
  not_arrived_at_port: 'not-arrived',
  inspection: 'inspection',
  inspection_cleared: 'inspection-cleared',
  empty_return_reported: 'empty-return-reported',
  transfer: 'transfer',
});

export function containerStatusTone(container) {
  if (!container || typeof container !== 'object') return 'neutral';
  if (container.status === 'empty_returned' || container.canonical_completed === true
    || container.ocean_status === 'empty_returned') return 'empty-returned';
  const oceanStatus = String(container.ocean_status || container.status || '')
    .trim().toLowerCase().replaceAll('-', '_');
  return CONTAINER_STATUS_TONES[oceanStatus] || 'neutral';
}

function sortableDateMillis(value) {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (value && typeof value.toMillis === 'function') {
    const millis = Number(value.toMillis());
    return Number.isFinite(millis) ? millis : null;
  }
  if (value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime()) ? date.getTime() : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const millis = new Date(value).getTime();
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
}

/**
 * Rows by a date, rows without one last either way.
 *
 * A heading click is newest first. The appointment worklists are soonest first
 * (Alan, 2026-09-26: "早的在上") - the Loaded and 可提 chips and the split's two
 * panes - because the row to act on next is today's, not the one furthest out.
 */
export function sortRowsByDate(rows, columnKey, { ascending = false } = {}) {
  if (!Array.isArray(rows) || typeof columnKey !== 'string' || !columnKey) return [];
  return rows.map((row, index) => ({
    row,
    index,
    millis: sortableDateMillis(row?.[columnKey]),
  })).sort((left, right) => {
    if (left.millis === null && right.millis === null) return left.index - right.index;
    if (left.millis === null) return 1;
    if (right.millis === null) return -1;
    const order = ascending ? left.millis - right.millis : right.millis - left.millis;
    return order || left.index - right.index;
  }).map(({ row }) => row);
}

export function sortRowsByNewestDate(rows, columnKey) {
  return sortRowsByDate(rows, columnKey);
}

// The table's resting order is the order the desk works in: whatever is already
// late first, then everything still owed, then the finished work out of the way at
// the bottom (Alan, 2026-08-01). Within a band the incoming order is preserved, so
// this only ever moves rows between bands. Picking a date column to sort by still
// overrides it entirely.
const STATUS_BAND_PAST_DUE = 0;
const STATUS_BAND_PENDING = 1;
const STATUS_BAND_DONE = 2;

export function containerStatusBand(row) {
  if (!row || typeof row !== 'object') return STATUS_BAND_PENDING;
  const status = containerStatusPresentation(row.status ?? row.ocean_status);
  if (row.is_past_due === true || status.token === 'past_due') return STATUS_BAND_PAST_DUE;
  // The Ocean Status is read as well as the Container status. Empty Returned is the
  // job finished (Alan, 2026-08-03), and the driver sets it on the mobile app long
  // before anything moves row.status - so reading only row.status left finished
  // Containers sitting in the middle of the desk's list.
  const ocean = containerStatusPresentation(row.ocean_status);
  if (status.terminal || ocean.terminal || row.canonical_completed === true
    || row.is_empty_returned === true) return STATUS_BAND_DONE;
  return STATUS_BAND_PENDING;
}

/**
 * Whether this Container is on a driver's phone right now.
 *
 * Alan defined it himself (2026-08-21): "已派单的就是这个柜子已被 release 到 driver
 * 出现在 drive 端了". Not "was dispatched once" - `dispatch_released` is a one-way
 * flag that a cancellation never clears - and not the Container's own task
 * pointer either. FFAU8502440 carries NO pointer while two live tasks sit on
 * ERIC's phone, so the row said "not dispatched" about work that was out.
 *
 * The set is answered by the callable from the dispatch tasks themselves and
 * arrives on the summary, so it cannot disagree with the phones. It is also what
 * makes the mark clear itself: a task that leaves an active status leaves the
 * set, which is exactly "当司机改完状态完成后" - no date to compare and no second
 * definition of "done" to keep in step.
 *
 * The fallback is the row's own summary, for a client talking to a backend that
 * does not send the set yet.
 *
 * Deliberately NOT gated on the Container being unfinished. It was, for half a
 * day, and it was wrong: all three Containers genuinely on a phone are already
 * `ocean_status: empty_returned`, so that guard hid exactly the rows Alan was
 * pointing at. Finished-looking work still on a driver's phone is the thing worth
 * marking, not the thing to hide.
 */
export function rowAwaitsDriver(row, activeDispatchIds = null) {
  if (!row || typeof row !== 'object') return false;
  if (activeDispatchIds instanceof Set) return activeDispatchIds.has(row.id);
  return row.dispatch_summary?.state === 'active';
}

/**
 * The table's default order: the approved Ocean Status sequence, each status
 * sorted by the date that governs it (ocean-status-order.mjs).
 *
 * This used to be a three-band sort - past due, then everything else, then done -
 * and it ran on the rows AFTER the dashboard had already sorted them, so it
 * silently threw that order away. Every past-due row went to the top whatever its
 * Ocean Status was, which cut each status block into a past-due half at the top
 * of the table and a pending half further down. Alan reported the order missing
 * four times and this was why: three 已报空 rows sitting below the 查验 block were
 * simply the ones whose status is `pending` rather than `past_due`.
 *
 * Urgency has not been thrown away with the bands - it is still on the row, in
 * the tone class and the status rail. What it no longer does is reorder the
 * table out from under the sequence Alan reads it by.
 */
export function sortRowsByStatusBand(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort(compareByOceanStatusOrder);
}

/**
 * Rows in the order the numbers were searched (Alan, 2026-10-17: "搜索出来的柜子要按照搜索柜子的顺序来排序"): a pasted
 * list reads back down the table in the list's own order, so it can be checked line by line against the sheet it
 * came from. A row matching none of the terms keeps its place after the rest. Stable, so ties keep `rows` order.
 */
export function sortRowsBySearchOrder(rows, terms) {
  if (!Array.isArray(rows)) return [];
  const wanted = (Array.isArray(terms) ? terms : []).map((term) => String(term ?? '').trim().toUpperCase()).filter(Boolean);
  if (wanted.length < 2) return [...rows];
  const rank = (row) => {
    const number = String(row?.container_no ?? '').replace(/\s+/gu, '').toUpperCase();
    const index = wanted.findIndex((term) => number.includes(term));
    return index < 0 ? wanted.length : index;
  };
  return rows.map((row, index) => ({ row, index, rank: rank(row) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(({ row }) => row);
}

export function nextDateSortKey(currentKey, clickedKey) {
  const selected = typeof clickedKey === 'string' ? clickedKey.trim() : '';
  if (!selected) return null;
  return currentKey === selected ? null : selected;
}

export function dateSortHeaderState(column, currentKey, { ascending = false } = {}) {
  const sortable = column?.format === 'date-only' || column?.format === 'date-time';
  const active = sortable && currentKey === column.key;
  const label = String(column?.label || column?.headerLabel || column?.key || 'date');
  return Object.freeze({
    sortable,
    active,
    ariaPressed: String(active),
    ariaSort: active ? (ascending ? 'ascending' : 'descending') : 'none',
    ariaLabel: active
      ? `Restore original order for ${label}`
      : `Sort ${label} from newest to oldest`,
    title: active ? 'Original order' : 'Newest to oldest',
  });
}

export function columnUsesEditSurface(column, role) {
  if (columnEditableForRole(column, role)) return true;
  return column?.interaction === 'pod'
    && ['admin', 'dispatcher'].includes(String(role || '').trim().toLowerCase());
}

export function scrollbarThumbMetrics({
  clientWidth, trackWidth = clientWidth, scrollWidth, scrollLeft, minThumbWidth = 44,
}) {
  const viewport = Math.max(0, Number(clientWidth) || 0);
  const track = Math.max(0, Number(trackWidth) || viewport);
  const content = Math.max(viewport, Number(scrollWidth) || 0);
  const maxScroll = Math.max(0, content - viewport);
  const thumbWidth = maxScroll === 0
    ? track
    : Math.min(track, Math.max(minThumbWidth, (viewport / content) * track));
  const maxThumbLeft = Math.max(0, track - thumbWidth);
  const normalizedScroll = Math.min(maxScroll, Math.max(0, Number(scrollLeft) || 0));
  const thumbLeft = maxScroll === 0 ? 0 : (normalizedScroll / maxScroll) * maxThumbLeft;
  return { thumbWidth, thumbLeft, maxScroll, maxThumbLeft };
}

export function mountTableDragPan(table, { threshold = 5, locked = () => false } = {}) {
  if (!table?.addEventListener || !table?.removeEventListener) return () => {};
  const safeThreshold = Number.isFinite(threshold) && threshold >= 0 ? threshold : 5;
  let drag = null;
  let swallowClick = false;

  const finish = (event = null) => {
    if (!drag || (event?.pointerId !== undefined && event.pointerId !== drag.pointerId)) return;
    // A pan that actually moved must not also count as a click on whatever was
    // under the finger when it started, or dragging the table sideways from a
    // Container number would copy it.
    if (drag.active) swallowClick = true;
    table.releasePointerCapture?.(drag.pointerId);
    drag = null;
    delete table.dataset?.panDragging;
  };
  const begin = (event) => {
    if (event.button !== 0 || event.pointerType === 'touch') return;
    const cell = event.target?.closest?.('td, th');
    // Locked, the cell controls are inert - every editable cell is BUILT as a
    // button because that is what makes the lock instant, so refusing to pan
    // from a button meant the table could not be dragged anywhere except its
    // handful of plain-text cells (Alan, 2026-08-11: "点击表格拖动时...拖动不了").
    // Unlocked they are real controls again and keep the pointer to themselves.
    const interactiveSelector = locked()
      ? '[data-no-table-pan]'
      : 'a, button, input, textarea, select, label, [contenteditable="true"], [role="button"], [data-no-table-pan]';
    const interactive = event.target?.closest?.(interactiveSelector);
    if (!cell || interactive) return;
    drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScroll: table.scrollLeft,
      active: false,
    };
    // Capture is taken in move(), once the drag is real - NOT here. Capturing on
    // pointerdown retargets the pointerup, which can move the click off the
    // button it started on, and now that a pan may start on a cell button that
    // would break click-to-copy. A press that never moves must stay an ordinary
    // click on whatever it was pressed on.
  };
  const move = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.active) {
      if (Math.abs(deltaX) <= safeThreshold) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        finish(event);
        return;
      }
      drag.active = true;
      table.setPointerCapture?.(drag.pointerId);
      table.dataset.panDragging = 'true';
    }
    table.scrollLeft = drag.startScroll - deltaX;
    event.preventDefault?.();
  };
  const end = (event) => finish(event);
  // Capture, so it runs before the table's own click handler and before any
  // control's default. Exactly one click is swallowed, and only after a pan that
  // passed the threshold - a plain click never sets the flag, so click-to-copy
  // still works while locked.
  const clickAfterPan = (event) => {
    if (!swallowClick) return;
    swallowClick = false;
    event.stopPropagation();
    event.preventDefault();
  };

  table.addEventListener('pointerdown', begin);
  table.addEventListener('pointermove', move);
  table.addEventListener('pointerup', end);
  table.addEventListener('pointercancel', end);
  table.addEventListener('lostpointercapture', end);
  table.addEventListener('click', clickAfterPan, true);

  return () => {
    finish();
    swallowClick = false;
    table.removeEventListener('pointerdown', begin);
    table.removeEventListener('pointermove', move);
    table.removeEventListener('pointerup', end);
    table.removeEventListener('pointercancel', end);
    table.removeEventListener('lostpointercapture', end);
    table.removeEventListener('click', clickAfterPan, true);
  };
}

export function nearEndAnchorVisible(rect, viewportHeight, margin = 240) {
  if (!rect || !Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)
    || !Number.isFinite(viewportHeight) || viewportHeight <= 0) return false;
  const safeMargin = Number.isFinite(margin) && margin >= 0 ? margin : 240;
  // Once the user has reached or passed the prefetch anchor, keep loading the
  // next page. Requiring the anchor to remain visible strands fast scrolls at
  // the previous page boundary (for example, 45 of 50 rows).
  return rect.top <= viewportHeight + safeMargin;
}

const DATE_ONLY_TEXT = /^\d{4}-\d{2}-\d{2}$/;

export function isDateColumn(column) {
  return column?.format === 'date-only' || column?.format === 'date-time';
}

/** True only when the stored value really carries a time of day. */
export function dateCellHasTime(column, row) {
  if (column?.format !== 'date-time') return false;
  const precision = row?.[`${column.key}_precision`];
  if (precision) return String(precision).toLowerCase() === 'date_time';
  const raw = row?.[column.key];
  if (typeof raw === 'string' && DATE_ONLY_TEXT.test(raw.trim())) return false;
  // Pickup Appt can be a date with no time, stored as that day at local midnight.
  if (TYPED_DATE_TIME_FIELDS.has(column.key) && isLocalMidnight(sortableDateMillis(raw))) return false;
  return sortableDateMillis(raw) !== null;
}

/** Read-mode text: the date alone, date plus time when there is one, em dash when empty. */
export function dateCellDisplayText(column, row, format) {
  if (typeof format !== 'function' || sortableDateMillis(row?.[column?.key]) === null) return '—';
  const effective = dateCellHasTime(column, row) ? column : { ...column, format: 'date-only' };
  const text = String(format(effective, row) ?? '').trim();
  return text || '—';
}

/**
 * A date cell is text until you click it, and the native control after.
 *
 * It used to BE the native control whenever the column was editable, which cost
 * nothing while editing was a mode you turned on for a moment. Editing is now
 * permanent (Alan, 2026-08-08), and that turned every date cell in the table into
 * a live <input type="date"> - nine date columns over 452 Containers, each input
 * a shadow tree with its own spin buttons and calendar, all of it rebuilt on
 * every repaint. That is the lag Alan reported the next morning ("默认就有 edit
 * 的话更卡了"). One click still opens the calendar; the difference is that the
 * calendar now exists for one cell instead of for two thousand.
 */
export function dateCellMarkup({ column, row, editable, display }) {
  if (!editable) return `<span class="dashboard-date-value"><span>${escapeHtml(display)}</span></span>`;
  return `<button type="button" class="dashboard-date-value dashboard-date-value--edit" data-open-date-input data-container-id="${escapeHtml(row.id)}" data-field="${escapeHtml(column.key)}" aria-label="${escapeHtml(column.label)}"><span>${escapeHtml(display)}</span></button>`;
}

/** The native control itself, built for the one cell the operator opened. */
export function dateInputMarkup({ column, row }) {
  const millis = sortableDateMillis(row?.[column.key]);
  const input = dashboardEditorInputSpec(column, millis === null ? null : new Date(millis));
  const typed = input.type === 'text'
    ? ` placeholder="${escapeHtml(input.placeholder ?? '')}" autocomplete="off" spellcheck="false" data-original="${escapeHtml(input.value)}"`
    : '';
  return `<input class="dashboard-date-input" data-date-input data-container-id="${escapeHtml(row.id)}" data-field="${escapeHtml(column.key)}" type="${input.type}" value="${escapeHtml(input.value)}" aria-label="${escapeHtml(column.label)}"${typed}>`;
}

// One word in both locales - Alan asked for the menu to stay spare, so the free-text
// escape hatch just reads "Custom" (2026-08-01).
export function customAddressLabel() {
  return 'Custom';
}

export function customAddressChipMarkup(locale) {
  return `<span class="adaptive-data-chip adaptive-data-chip--custom-address" title="Custom / 自定义">${escapeHtml(customAddressLabel(locale))}</span>`;
}

const ADDRESS_GROUP_LABELS = Object.freeze({
  customer: 'Assigned Addresses',
  terminal: 'Terminals',
  other: 'Other Addresses',
});

export function addressGroupLabel(group) {
  return ADDRESS_GROUP_LABELS[group] || optionLabel(group);
}

function addressCatalogGroups(catalog) {
  const groups = [];
  for (const option of catalog.options) {
    const group = String(option?.group || '');
    const last = groups.at(-1);
    if (last && last.group === group) last.options.push(option);
    else groups.push({ group, options: [option] });
  }
  return groups;
}

/**
 * A dispatcher looking at forty rows from a dozen customers was offered every
 * customer's warehouses in one list. An address priced for one customer is not a
 * valid choice for another, so the assigned-address group is narrowed to the row's
 * own customer (Alan, 2026-08-01). Terminals and other addresses are shared and
 * stay. Rows with no customer name, and catalogs with no customer attribution at
 * all, keep the full list rather than silently emptying the dropdown.
 */
// `strict` is the Delivery Address menu (Alan, 2026-10-30): only that customer's own addresses - terminals and the
// addresses typed on other Containers are no delivery choice - plus the address the row already holds, so its current
// value never drops out of its own menu.
function scopeCatalogToRowCustomer(catalog, row, { strict = false, current = '' } = {}) {
  const rowCustomer = customerMatchKey(row?.customer_name);
  if (!rowCustomer) return catalog;
  const attributed = catalog.options.filter((option) => option.customerKey);
  if (!attributed.length) return catalog;
  const currentKey = customerMatchKey(current);
  const options = catalog.options.filter((option) => (
    strict
      ? option.customerKey === rowCustomer || (currentKey && customerMatchKey(option.address) === currentKey)
      : !option.customerKey || option.customerKey === rowCustomer
  ));
  if (options.length === catalog.options.length) return catalog;
  const index = new Map(options.map((option) => [customerMatchKey(option.address), option]));
  const find = (addressText) => index.get(customerMatchKey(addressText)) ?? null;
  return Object.freeze({
    options: Object.freeze(options),
    has: (addressText) => find(addressText) !== null,
    find,
  });
}

/**
 * Empty To is where the empty container goes back, which is always a terminal, so
 * it picks from the terminal list rather than the customer's warehouses
 * (Alan, 2026-08-01). Falls back to the whole catalog if no terminal is attributed,
 * so the control never renders as an empty menu.
 */
function scopeCatalogToTerminals(catalog) {
  const options = catalog.options.filter((option) => option.group === 'terminal');
  if (!options.length) return catalog;
  const index = new Map(options.map((option) => [customerMatchKey(option.address), option]));
  const find = (addressText) => index.get(customerMatchKey(addressText)) ?? null;
  return Object.freeze({
    options: Object.freeze(options),
    has: (addressText) => find(addressText) !== null,
    find,
  });
}

/**
 * Every address column edits through one control: the assigned addresses grouped
 * by catalog group plus a final Custom entry that reveals a free-form input. The
 * saved value is always the address text, never the Custom sentinel.
 */
export function addressEditorMarkup({ column, row, catalog, locale }) {
  const value = normalizeAddressText(row?.[column.key]);
  const label = escapeHtml(column.label);
  const anchor = `data-address-editor data-container-id="${escapeHtml(row.id)}" data-field="${escapeHtml(column.key)}"`;
  const customInput = (customValue, hidden) => `<input class="dashboard-address-custom" data-address-custom type="text" autocomplete="off" aria-label="${label}" value="${escapeHtml(customValue)}"${hidden ? ' hidden' : ''}>`;
  if (!catalog || !Array.isArray(catalog.options) || catalog.options.length === 0) {
    // A box with no list, and until 2026-08-21 it said nothing about why.
    //
    // Alan asked to "点击delivery address column可以直接勾新的" - to tick one from
    // the approved list - and this branch hands over a plain text box instead. On
    // a cell that already shows the address, that reads as "clicking does nothing"
    // ("现在还是点击没有反应"), which is what he reported. The box still works,
    // because typing an address is better than being stuck, but the missing list
    // now says it is missing rather than looking like a dead click.
    //
    // Every customer in production has approved addresses (26 of 26 on
    // 2026-08-21), so an empty catalog here means the LIST did not load, not that
    // there is nothing to load.
    return `<span class="dashboard-address-cell" ${anchor}>${customInput(value, false)}`
      + '<small class="dashboard-address-note">Approved list unavailable — type the address. '
      + '已批准地址列表没加载出来，请直接输入。</small></span>';
  }
  // Empty To is the one closed list: the empty goes back to a terminal or the
  // return is wrong, so there is nothing to type (Alan, 2026-08-05). No Custom
  // entry, no free-form input beside it — just the terminals.
  const closedList = column.key === 'empty_to_address';
  catalog = closedList
    ? scopeCatalogToTerminals(catalog)
    : scopeCatalogToRowCustomer(catalog, row, { strict: column.key === 'delivery_warehouse_address', current: value });
  const custom = !closedList && isCustomAddress(catalog, value);
  const optionMarkup = (option) => {
    const address = normalizeAddressText(option?.address ?? option?.value);
    const selected = !custom && address === value ? ' selected' : '';
    return `<option value="${escapeHtml(address)}"${selected}>${escapeHtml(option?.label || address)}</option>`;
  };
  const body = addressCatalogGroups(catalog).map(({ group, options }) => (group
    ? `<optgroup label="${escapeHtml(addressGroupLabel(group))}">${options.map(optionMarkup).join('')}</optgroup>`
    : options.map(optionMarkup).join(''))).join('');
  const customOption = closedList
    ? ''
    : `<option value="${escapeHtml(CUSTOM_ADDRESS_VALUE)}"${custom ? ' selected' : ''}>${escapeHtml(customAddressLabel(locale))}</option>`;
  return `<span class="dashboard-address-cell" ${anchor}><select class="dashboard-address-select" data-address-select aria-label="${label}"><option value=""${value ? '' : ' selected'}>—</option>${body}${customOption}</select>${closedList ? '' : customInput(custom ? value : '', !custom)}</span>`;
}

/** Read-mode address cell: the map trigger plus a Custom chip for off-catalog text. */
export function addressCellMarkup({ column, row, catalog, locale, trigger }) {
  const custom = catalog ? isCustomAddress(catalog, normalizeAddressText(row?.[column.key])) : false;
  return `<span class="dashboard-address-cell${custom ? ' is-custom-address' : ''}">${trigger}${custom ? customAddressChipMarkup(locale) : ''}</span>`;
}

const CUSTOMER_PAID_OPTION = 'customer_paid';

// Moved to container-field-registry.mjs, where the pairing can reach it without
// importing this whole module. Re-exported rather than relocated in the callers,
// so the four test files and any other reader keep one working import path and
// there is still exactly one definition.
export { rowUsesCustomerOwnChassis };

/**
 * Column permission is per role; a couple of rules are per ROW as well. Today that
 * is Chassis #: if the customer brings their own chassis there is nothing for us to
 * assign, so the cell is not editable on that row (Alan, 2026-08-02). The same rule
 * is enforced in the callable - this only decides whether a control is drawn.
 */
// Once CTF or TMF is Paid the Container is billed and stops being editable by
// anyone (Alan, 2026-08-03). The customer keeps their own Note and Report Empty,
// and Finance keeps the two fee statuses so a mis-click can be undone. This only
// decides whether the control is drawn - buildDashboardFieldUpdatePlan is what
// actually refuses the write.
// The customer's two doors after billing, plus the fee columns themselves. The
// three fee entries are vestigial since 2026-08-17 - a customer cannot edit a fee
// status at any time - and are kept only so the set still reads as "what a billed
// Container leaves open" if the lock ever widens back over staff.
const FEE_SETTLED_OPEN_FIELDS = new Set([
  'customer_note', 'reported_empty_at', 'ctf_status', 'tmf_status', 'ctf_tmf_status',
]);

export function rowFeesSettled(row) {
  return row?.ctf_status === 'paid' || row?.tmf_status === 'paid';
}

// The customer's own window closes when the ETA is entered (Alan, 2026-08-07).
// Until then the Container is their booking and they may correct anything they
// typed; once ops has an ETA the Container has been planned against, and a
// customer moving the box number or the delivery address underneath that plan
// moves work that has already been scheduled.
//
// Their Note and Report Empty stay open, the same two doors the billing lock
// leaves open and for the same reason: reporting the box empty is work that
// happens long after the ETA, so closing it would break the empty return.
const ETA_LOCK_CUSTOMER_OPEN_FIELDS = new Set(['customer_note', 'reported_empty_at']);

export function rowEtaEntered(row) {
  const eta = row?.eta_at;
  if (eta === null || eta === undefined || eta === '') return false;
  const parsed = eta instanceof Date ? eta : new Date(eta);
  return Number.isFinite(parsed.getTime()) && parsed.getTime() > 0;
}

export function columnEditableForRow(column, role, row) {
  if (!columnEditableForRole(column, role)) return false;
  if (column?.key === 'chassis_no') return !rowUsesCustomerOwnChassis(row);
  // Only a Cancelled Container's number may change - except for Admin, whose reach over the
  // base fields is the whole point of the role (Alan, 2026-10-01).
  if (column?.key === 'container_no') return containerNumberEditable(row, role);
  if (String(role ?? '').trim().toLowerCase() === 'customer'
    && rowEtaEntered(row) && !ETA_LOCK_CUSTOMER_OPEN_FIELDS.has(column?.key)) return false;
  // Billing closes the customer's side only (Alan, 2026-08-17: "已开票后是客户不可以
  // 再次更改员工还可以"). It closed everyone until then, so a paid fee froze the row
  // for dispatch too - and the work goes on after billing. Mirrors the same
  // narrowing in functions/src/dashboard-field-update-service.ts; this list only
  // decides whether the control is drawn.
  if (String(role ?? '').trim().toLowerCase() === 'customer'
    && rowFeesSettled(row) && !FEE_SETTLED_OPEN_FIELDS.has(column?.key)) return false;
  return true;
}

export const HIGHLIGHT_COLORS = Object.freeze(['yellow', 'green', 'blue', 'pink', 'orange']);
const SELECT_BOX_PX = 24;

const NOTE_COLUMN_KEYS = new Set(['hold_note', 'customer_note']);
const NOTE_COLUMN_MAX_PX = 720;
// The Note grows down, not across (Alan, 2026-10-12: "note可以上下变宽但是不要变得太长"): at most 280px, the text
// wraps to three lines and the rest is in the hover. Measured on production's 544 notes: 354 fit one line at 280,
// 515 fit two, 523 three.
const WRAPPED_NOTE_MAX_PX = Object.freeze({ hold_note: 280 });
const noteColumnCapPx = (key) => WRAPPED_NOTE_MAX_PX[key] ?? NOTE_COLUMN_MAX_PX;

/**
 * The pixels one line of the longest note in `rows` needs (11px type: a wide character is
 * about 12px, anything else about 6.6px) plus the cell's padding. Zero when no row has one,
 * so an empty column keeps its normal width.
 */
export function noteColumnFitPx(rows, key) {
  let longest = 0;
  for (const row of Array.isArray(rows) ? rows : []) {
    // Customer Note shows only its newest line (the rest are folded into "+N"), so that is what it
    // has to fit - not the whole log.
    const raw = String(row?.[key] ?? '');
    const text = (key === 'customer_note' ? (raw.split(/\n+/).find((line) => line.trim()) ?? '') : raw)
      .replace(/\s+/gu, ' ').trim();
    if (!text) continue;
    let px = 0;
    for (const char of text) px += char.codePointAt(0) > 0x2e7f ? 12 : 6.6;
    if (px > longest) longest = px;
  }
  return longest ? Math.ceil(longest) + (key === 'customer_note' ? 52 : 20) : 0;
}

/**
 * Report Empty is one button, then a timestamp (Alan, 2026-08-02).
 * Delivered to Warehouse and not yet reported -> the button.
 * Reported -> the date and time it was pressed, which can be cancelled while the
 * Container is still Empty Return Reported. Once it has moved on the date is just
 * text, so a late cancel cannot drag a finished Container backwards.
 */
export function reportedEmptyCellMarkup({
  row, canReport, canCancel, display, anyStatus = false, place = '',
}) {
  const status = String(row?.ocean_status ?? row?.status ?? '').trim().toLowerCase();
  const reported = Boolean(row?.reported_empty_at);
  const anchor = `data-container-id="${escapeHtml(row?.id ?? '')}" data-field="reported_empty_at"`;
  // Reporting does not need Edit Mode - the button is there in the ordinary table
  // and one press does it (Alan, 2026-08-02). For a customer it still only appears
  // while the Container is Delivered to Warehouse, because that is the only point
  // at which there is an empty to report. Staff are not held to that (Alan,
  // 2026-09-29: "文员和调度所有人都有权利更改"): Clerk and Dispatch know when a box is
  // empty before the system does, so for them it is there until the Container is
  // finished, and it asks where the empty is (data-report-empty-staff).
  if (!reported) {
    const finished = status === 'empty_returned';
    if (!canReport || finished || (!anyStatus && status !== 'delivered_to_warehouse')) {
      return '<span class="dashboard-cell-value">—</span>';
    }
    return `<button type="button" class="dashboard-report-empty" data-report-empty${anyStatus ? ' data-report-empty-staff' : ''} ${anchor}>Report Empty</button>`;
  }
  // Undoing it does need Edit Mode, and then a confirm - rolling a Container back a
  // status is not something a stray click on a date should do. Only while it is
  // still Empty Return Reported; once it has moved on the date is plain text.
  // What was typed with the report sits IN this cell, after the date ("10月1日 05:00 · 611") - it is not
  // copied to Empty From (Alan, 2026-10-11: "不会同步empty from，而是直接放到这个report empty这个格子").
  const shown = place ? `${display} · ${place}` : display;
  if (!canCancel || status !== 'empty_return_reported') {
    return `<span class="dashboard-cell-value" title="${escapeHtml(shown)}">${escapeHtml(shown)}</span>`;
  }
  return `<button type="button" class="dashboard-cell-value dashboard-cell-value--editable dashboard-report-empty-at" data-cancel-report-empty title="${escapeHtml(shown)}" ${anchor}>${escapeHtml(shown)}</button>`;
}

function syntheticEditorColumn(field) {
  if (field === 'ctf_status' || field === 'tmf_status') {
    return {
      key: field,
      label: field === 'ctf_status' ? 'CTF' : 'TMF',
      editor: 'select',
      editRoles: ['finance'],
      // Customer Paid is the third answer to this fee's own question, and it applies
      // to this fee alone (Alan, 2026-08-02): CTF can be Customer Paid while TMF is
      // still unpaid. It used to be redirected into a shared customer_paid flag that
      // set both at once, so the two could never disagree.
      options: ['paid', 'unpaid', CUSTOMER_PAID_OPTION],
    };
  }
  if (field === 'customer_paid') {
    return {
      key: field,
      label: 'Customer Payment',
      editor: 'select',
      editRoles: ['finance'],
      options: ['customer_paid'],
    };
  }
  return null;
}

function editorCurrentValue(row, column) {
  if (column.key === 'ctf_status' || column.key === 'tmf_status') {
    // Each fee answers for itself (Alan, 2026-08-02): one can be Customer Paid while
    // the other is still unpaid. The shared customer_paid flag used to outrank both
    // and force them to agree, which is exactly what made them impossible to separate.
    const explicit = row[column.key];
    if (explicit) return explicit;
    const values = String(row.ctf_tmf_status || '').split('/').map((item) => item.trim().toLowerCase());
    return column.key === 'ctf_status' ? values[0] || '' : values[1] || '';
  }
  if (column.key === 'customer_paid') {
    const fallback = String(row.ctf_tmf_status || '').split('/')[2]?.trim().toLowerCase();
    return row.customer_paid === true || fallback === 'customer_paid' ? 'customer_paid' : '';
  }
  return row[column.key] ?? '';
}

// The menu entry that means "none of these".
//
// size_type has carried it as a literal option since the beginning; ssl and
// terminal_name (2026-08-21) carry `allowCustom` instead and have it appended,
// because their options are a census of real values and CUSTOM is not one of
// them. One token either way, so one handler.
const CUSTOM_OPTION_VALUE = 'CUSTOM';

function menuOptions(column) {
  // fieldOptions is the seeded list plus everything the fleet has taught it, so
  // a value someone typed into a Custom box last week is an ordinary option now.
  const declared = fieldOptions(column);
  return column?.allowCustom && !declared.includes(CUSTOM_OPTION_VALUE)
    ? [...declared, CUSTOM_OPTION_VALUE]
    : declared;
}

// Whether what is stored is something the menu does not list - which is the state
// the Custom entry has to show as selected, or reopening the editor on a value
// somebody typed reads as though nothing is set.
function columnHoldsCustomValue(column, value) {
  if (!column?.allowCustom) return false;
  const stored = String(value ?? '').trim();
  if (!stored) return false;
  return !menuOptions(column).some((option) => option === stored);
}

function customOptionLabel(column) {
  return column?.key === 'size_type' ? 'CUSTOM' : 'Custom…';
}

// The box the Custom entry opens. Pre-filled when what is stored is already a
// custom value, so reopening it is an edit rather than a retype.
function customValueMarkup(column, value) {
  const stored = String(value ?? '').trim();
  const listed = menuOptions(column).some((option) => option === stored);
  const placeholder = column?.key === 'size_type' ? 'e.g. 53 Reefer'
    : column?.key === 'ssl' ? 'e.g. PIL'
      : column?.key === 'terminal_name' ? 'e.g. PIER G' : '';
  return `<label class="dashboard-custom-type-editor"><span>${escapeHtml(customEditorLabel(column))}</span><input data-editor-input data-custom-option-input type="text" maxlength="40" autocomplete="off" placeholder="${escapeHtml(placeholder)}" value="${escapeHtml(listed ? '' : stored)}"></label>`;
}

function customEditorLabel(column) {
  return column?.key === 'size_type' ? 'Custom container type' : `Custom ${column?.label ?? ''}`.trim();
}

export function editorControlMarkup(column, value, precision = null) {
  if (column.editor === 'select') {
    const customValue = columnHoldsCustomValue(column, value);
    // The last option clears (Alan, 2026-08-16: a role that may change a column
    // may empty it). A menu is the one editor with nothing to backspace, so it
    // needs an option that means "none" - and it commits "" through the same
    // click handler as the rest, so there is no second path to keep in step.
    const clearOption = `<button type="button" role="option" class="dashboard-floating-option dashboard-floating-option--clear" data-editor-option="" aria-selected="${!String(value ?? '').trim()}">—</button>`;
    return `<div class="dashboard-floating-options" role="listbox" aria-label="${escapeHtml(column.label)}">${menuOptions(column).map((option) => `<button type="button" role="option" class="dashboard-floating-option dashboard-floating-option--${optionSlug(option)}" data-editor-option="${escapeHtml(option)}"${option === CUSTOM_OPTION_VALUE && column.allowCustom ? ' data-custom-option' : ''} aria-selected="${String(value) === option || (option === CUSTOM_OPTION_VALUE && customValue)}">${escapeHtml(option === CUSTOM_OPTION_VALUE ? customOptionLabel(column) : optionLabel(option, column))}</button>`).join('')}${clearOption}</div>`;
  }
  if (column.editor === 'actual_empty_return') {
    const date = dashboardEditorInputSpec({ ...column, editor: 'date', format: 'date-only' }, value).value;
    const time = precision === 'date_time'
      ? dashboardEditorInputSpec({ ...column, editor: 'date', format: 'date-time' }, value).value.slice(11)
      : '';
    return `<div class="dashboard-cell-editor-control"><span>${escapeHtml(column.label)}</span><input data-editor-date type="date" required value="${escapeHtml(date)}"><input data-editor-time type="time" value="${escapeHtml(time)}"></div>`;
  }
  if (column.editor === 'currency') {
    const dollars = Number.isSafeInteger(value) ? (value / 100).toFixed(2) : '';
    return `<label class="dashboard-cell-editor-control"><span>${escapeHtml(column.label)}</span><input data-editor-input inputmode="decimal" type="number" min="0" step="0.01" value="${escapeHtml(dollars)}"></label>`;
  }
  if (column.key === 'weight_lbs') {
    // Pounds is the default and the stored unit; KG is an input convenience that is
    // converted on Enter (Alan, 2026-08-03). The toggle is on screen rather than
    // implied, so nobody types kilograms into a box that says LBS.
    return `<div class="dashboard-cell-editor-control dashboard-weight-editor"><span>${escapeHtml(column.label)}</span><div class="dashboard-weight-editor__row"><input data-editor-input inputmode="decimal" type="number" min="0" step="any" value="${escapeHtml(value)}"><div class="dashboard-weight-units" role="group" aria-label="Weight unit"><button type="button" data-weight-unit="lbs" aria-pressed="true">LBS</button><button type="button" data-weight-unit="kg" aria-pressed="false">KG</button></div></div><small class="dashboard-weight-editor__hint" data-weight-hint>Stored in pounds.</small></div>`;
  }
  if (column.editor === 'number') {
    return `<label class="dashboard-cell-editor-control"><span>${escapeHtml(column.label)}</span><input data-editor-input inputmode="decimal" type="number" min="0" step="any" value="${escapeHtml(value)}"></label>`;
  }
  if (column.editor === 'note' && !APPEND_ONLY_NOTE_FIELDS.has(column.key)) {
    // An ordinary note opens holding its text, to be changed or emptied like any
    // text box (Alan, 2026-09-25, of Hold: "可以直接更改或者删除就是和普通text box
    // 一样"). The four fee notes were never append-only on the server either, and
    // opening them empty meant a save replaced the whole description with the
    // words just typed - none had been written yet, so nothing was lost.
    return `<div class="dashboard-cell-editor-control dashboard-note-editor"><span>${escapeHtml(column.label)}</span><textarea data-editor-input rows="4">${escapeHtml(value ?? '')}</textarea></div>`;
  }
  if (column.editor === 'note') {
    // The box opens empty (Alan, 2026-08-11: "所有note只可以编辑不能删除里面的原有
    // 东西"). It used to open holding the note, which is what made erasing it a
    // single select-all away. What is already written sits above, readable and
    // selectable but outside the form, and what is typed here is added on top of
    // it by the server. There is no control here that can take a word away.
    // Only the customer's note is append-only now; see APPEND_ONLY_NOTE_FIELDS.
    const kept = String(value ?? '').trim();
    return `<div class="dashboard-cell-editor-control dashboard-note-editor"><span>${escapeHtml(column.label)}</span>${
      kept ? `<div class="dashboard-note-editor__kept" tabindex="0" role="note" aria-label="${escapeHtml(`${column.label} so far`)}">${escapeHtml(kept)}</div>` : ''
    }<textarea data-editor-input rows="3" placeholder="${kept ? 'Add to this note' : 'Write the note'}"></textarea></div>`;
  }
  return `<label class="dashboard-cell-editor-control"><span>${escapeHtml(column.label)}</span><input data-editor-input type="text" value="${escapeHtml(value)}"></label>`;
}


/**
 * How many rows exist in the DOM at once.
 *
 * The table used to put every row in the document: 452 Containers by 35 columns
 * is 15,820 cells and 4.7 MB of markup, and the browser spent about a second
 * building it and two more re-parsing it on any change. Eighteen rows fit on a
 * screen. The rest were paid for on every render, every edit and every scroll -
 * which is why clicking, editing and even dragging the table felt slow (Alan,
 * 2026-08-09: "就算只是拖动都很卡").
 *
 * Only the rows near the viewport are built. The ones above and below are two
 * spacer rows holding their exact height, so the page is the same length, the
 * scrollbar is the same size, and scrolling lands where it always did.
 */
export const ROW_WINDOW_BUFFER = 24;
/**
 * The margin the window keeps once the table is on screen.
 *
 * The page scrolls on the compositor thread and the scroll event reaches this
 * code a frame or so later, so a margin narrow enough to be crossed inside that
 * gap shows blank paper. Sixty rows is about 1,560px either side - more than a
 * wheel notch or a trackpad flick covers in a frame. It costs nothing per frame
 * because the repaint below is incremental; it costs only the first paint, which
 * is why the first render uses the tight buffer above and widens afterwards.
 */
export const ROW_WINDOW_SETTLED_BUFFER = 60;
// Below this a full table is cheaper than the bookkeeping.
export const ROW_WINDOW_MINIMUM = 80;

/** How a Container number is matched against the copied list: case and spacing free. */
export function normalizedCopyKey(value) {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

export function rowWindowBounds({ total, firstVisible, visibleCount, buffer = ROW_WINDOW_BUFFER }) {
  const count = Math.max(0, Number(total) || 0);
  if (count <= ROW_WINDOW_MINIMUM) return { start: 0, end: count };
  const first = Math.min(Math.max(0, Math.floor(Number(firstVisible) || 0)), Math.max(0, count - 1));
  const onScreen = Math.max(1, Math.ceil(Number(visibleCount) || 1));
  const start = Math.max(0, first - buffer);
  const end = Math.min(count, first + onScreen + buffer);
  return { start, end: Math.max(end, start + 1) };
}

/** Mounts the responsive, high-density Container workspace. */
export function mountAdaptiveContainerTable({
  host,
  rows,
  role,
  userKey = `${role}:dashboard`,
  // Draw only these columns (plus container_no). Null means every authorized
  // column, which is what the Dashboard's own table wants. See
  // narrowToRequestedColumns.
  columnKeys = null,
  // Pin the layout tier instead of measuring it. Null means measure, which is
  // what the Dashboard's own table wants. See the tierController below.
  forceTier = null,
  preferenceStorage = null,
  formatCell,
  renderActionCell = () => '',
  onNearEnd = () => {},
  onSaveField = async () => {},
  onOpenPod = () => {},
  onOpenPodEditor = () => {},
  onInspectRow = () => {},
  loadDriverOptions = async () => [],
  addressCatalog = null,
  onEditorError = () => {},
  // Picking an Empty # can be answered by ANOTHER table (Alan, 2026-09-30): under the
  // split this table is the left pane, which holds no empties, so the pick is handed
  // to the right pane and any row clicked there fills this cell. Returns true when it
  // took the pick; otherwise the table narrows itself as it always did.
  onBeginEmptyPick = null,
  onCopyFeedback = () => {},
  // Where the copied marks are kept, so they last until 清除标记 is pressed - across a reload, a split and back,
  // a new search (Alan, 2026-10-17: "只有点击按钮才能取消highlight不然会一直存在"). One key for every pane.
  copiedMarksStorage = null,
  copiedMarksKey = '',
  navigate = (hash) => { globalThis.location.hash = hash; },
  clipboard = globalThis.navigator?.clipboard,
  ResizeObserverClass = globalThis.ResizeObserver,
  IntersectionObserverClass = globalThis.IntersectionObserver,
}) {
  if (!(host instanceof Element) || !Array.isArray(rows)
    || typeof formatCell !== 'function' || typeof renderActionCell !== 'function'
    || typeof onNearEnd !== 'function' || typeof onSaveField !== 'function'
    || typeof onOpenPod !== 'function' || typeof onOpenPodEditor !== 'function'
    || typeof onInspectRow !== 'function' || typeof loadDriverOptions !== 'function') {
    throw new TypeError('Adaptive Container workspace options are invalid.');
  }
  if (!authorizedColumns(role).some(({ key }) => key === 'container_no')) {
    throw new TypeError('Adaptive Container workspace requires an authorized container_no column.');
  }
  // Kept as a LIST, not a Set: the order is the point.
  const requestedColumnKeys = Array.isArray(columnKeys) && columnKeys.length
    ? [...new Set(columnKeys)] : null;
  let resolvedPreferenceStorage = preferenceStorage;
  if (!resolvedPreferenceStorage) {
    try { resolvedPreferenceStorage = host.ownerDocument?.defaultView?.localStorage || null; } catch (_) { /* unavailable */ }
  }
  const columnPinStore = resolvedPreferenceStorage
    ? createDashboardColumnPinStore({
      storage: resolvedPreferenceStorage,
      userKey,
      columns: authorizedColumns(role),
    })
    : null;
  const state = {
    searchOrder: [],
    // Editing is always on (Alan, 2026-08-08): the Edit button was a mode you had
    // to remember to turn on before the table would accept a correction, and
    // turning it on rewrote thirteen thousand cells. A column the role may change
    // is marked in its header instead, so what is editable is a property of the
    // column rather than of the moment.
    rows: [...rows], role, editMode: true, highlightedId: null, markedRowId: null, destroyed: false,
    // What to say when there is nothing to draw. Set by the Dashboard, which is
    // the only thing that knows which filters are on.
    emptyReason: '',
    // Edit is a lock, not a mode (Alan, 2026-08-09: "edit应该只是一个锁就是防止误触
    // 的"). The cells are always built as their editable selves - that is what
    // makes the toggle instant - and the lock only decides whether they answer a
    // click. Locked on arrival, because its whole job is the accidental tap.
    locked: true,
    // Which rows the pairing flow has picked. The flow's own state lives in
    // app.js and the table had no idea, so clicking a row selected it and nothing
    // on screen changed (Alan, 2026-08-08).
    pairingPick: { ladenId: '', emptyId: '' },
    // The empty number drawn against the laden row while a pairing is being
    // chosen. Display only - no write happens until release, so cancelling is
    // free and a crash cannot leave a Container claiming an empty it never got.
    // `containerNo` is the single-table preview, where the value is read from the
    // OTHER row in this same table and the laden row itself is untouched.
    // `fields` is the split's, where the caller has already patched the row with
    // what the pairing will write and this only says which cells to mark.
    pairingPreview: { rowId: '', containerNo: '', fields: null },
    // Container numbers already sent to the clipboard, keyed the way the copy
    // list writes them so a case or spacing difference still matches.
    copiedNumbers: new Set((() => {
      try {
        const saved = copiedMarksKey ? JSON.parse(copiedMarksStorage?.getItem?.(copiedMarksKey) || '[]') : [];
        return Array.isArray(saved) ? saved.filter((value) => typeof value === 'string') : [];
      } catch (_) {
        return [];
      }
    })()),
    // A highlighter (Alan, 2026-10-01: "柜子的每个值可以像highlight颜色的"; earlier "每一个
    // 不同列的每一个格子点击可以暂时标记颜色"). With a colour picked, a click on any cell paints
    // THAT cell, not the row, and the same colour again lifts it. Temporary on purpose: it is
    // in memory only, so a reload or Clear wipes it and nothing is written to the Container.
    cellMarks: new Map(),
    highlightColor: null,
    // Select mode (Alan, 2026-10-11: "可以多选柜子然后可以批量的改状态"): a checkbox in each row's first
    // cell, and the ids ticked. The write itself is not here - the bulk bar (dashboard-bulk-edit.mjs)
    // reads selectedRows() and saves each one through the same path a typed cell uses.
    selectMode: false,
    selectedIds: new Set(),
    lastSelectedId: null,
    // The group the copy panel is currently sitting on, highlighted so you can
    // see which slice of the fleet you are about to take (Alan, 2026-08-11:
    // "切换组的时候...整个也会highlight...可以追踪到到底是在找那一组"). Separate
    // from copiedNumbers: this one is where you ARE, that one is where you have
    // BEEN, and stepping replaces it while copying accumulates.
    previewNumbers: new Set(),
    pinnedKeys: columnPinStore?.load() || [],
    sortDateKey: null,
    sortAscending: false,
    rowIdFilter: null,
    // Container ids a driver can see on their phone, from the summary. Null until
    // the first page lands, which is the safe direction: no mark rather than a
    // wrong one.
    activeDispatchIds: null,
  };
  const emptyPick = createEmptyContainerPickMode();
  // Set while THIS table is the one being pointed at on another table's behalf.
  let externalPick = null;
  // The Work Pool owns rowIdFilter too, so its value is put back rather than
  // cleared when the pick ends - otherwise picking an empty from inside the Work
  // Pool would quietly widen the table back to everything.
  let rowIdFilterBeforePick = null;
  // The row window, and what it needs to paint itself between full renders.
  let windowedRows = [];
  let windowedColumns = [];
  let rowWindow = { start: 0, end: 0 };
  let rowWindowBuffer = ROW_WINDOW_BUFFER;
  let rowHeightPx = 26;
  function readCopiedMarks() {
    if (!copiedMarksKey) return;
    try {
      const saved = JSON.parse(copiedMarksStorage?.getItem?.(copiedMarksKey) || '[]');
      state.copiedNumbers = new Set(Array.isArray(saved) ? saved.filter((value) => typeof value === 'string') : []);
    } catch (_) { /* a bad copy keeps what this table already had */ }
  }
  function saveCopiedMarks() {
    if (!copiedMarksKey) return;
    try {
      copiedMarksStorage?.setItem?.(copiedMarksKey, JSON.stringify([...state.copiedNumbers]));
    } catch (_) { /* private mode: the marks last as long as this page */ }
  }
  // What each row was last drawn at, by id. A wrapped Note makes rows of different heights (2026-10-12), and the
  // spacers stand in for rows that are not drawn: they sum the heights those rows really had - a row never drawn
  // counts as the average - so a row scrolled out of the window never shifts the rows still on screen.
  const drawnRowHeights = new Map();
  const heightOfRow = (row) => drawnRowHeights.get(row?.id) ?? rowHeightPx;
  function rowsHeightPx(rows, from, to) {
    let px = 0;
    for (let index = Math.max(0, from); index < Math.min(rows.length, to); index += 1) px += heightOfRow(rows[index]);
    return px;
  }
  // The (fractional) row index at a pixel offset from the top of the first row.
  function rowIndexAtOffset(rows, offset) {
    let px = 0;
    for (let index = 0; index < rows.length; index += 1) {
      const height = heightOfRow(rows[index]);
      if (px + height > offset) return index + (offset - px) / height;
      px += height;
    }
    return rows.length;
  }
  function rememberRowHeight(tr) {
    const id = tr?.dataset?.adaptiveRow;
    if (id) drawnRowHeights.set(id, tr.getBoundingClientRect().height);
  }
  let columnPinOffsets = new Map();
  let columnPinKeys = new Set();
  let lastPinnedColumnKey = 'container_no';
  /**
   * The cell currently being edited, and how wide its column has to become.
   *
   * Alan, 2026-08-11: "任何edit信息我都想要column会弹出来加宽然后text box在加宽的
   * 这一各部分。这样子第一工作人员不会失误判断到底是哪一个task". The editor used to
   * be a panel floating over the rows below it, which covered the very rows a
   * clerk checks to be sure they are on the right Container. So the column widens
   * where it stands, the box appears inside it, and the row underneath stays
   * highlighted the whole time - nothing moves and nothing is hidden.
   *
   * Kept here rather than in state because render() closes any open editor, so a
   * repaint can never be the thing that applies this.
   */
  let editingCell = null;
  // How much room each kind of editor needs to be usable. A narrowed date column
  // is 66px and a native date input needs about twice that - which is the clipped
  // "08/11/2" Alan photographed.
  const EDITOR_WIDTHS = Object.freeze({
    note: 260, address: 240, text: 200, currency: 150, number: 150, date: 150,
  });
  let rowWindowCleanup = () => {};
  let scrollbarCleanup = () => {};
  let nearEndObserver = null;
  let nearEndScrollCleanup = () => {};
  let driverOptionsPromise = null;
  let pendingRender = false;
  let nearEndCheck = () => {};
  const overlayManager = createOverlayManager({
    documentRef: host.ownerDocument,
    onAfterClose(reason) {
      if (reason === 'replace' || !pendingRender || state.destroyed) return;
      pendingRender = false;
      const viewWindow = host.ownerDocument?.defaultView;
      if (viewWindow?.requestAnimationFrame) {
        viewWindow.requestAnimationFrame(() => {
          if (!state.destroyed) render();
        });
      } else {
        render();
      }
    },
  });
  host.dataset.reviewId = 'container-workspace';
  // mountWorkspaceTier measures and fires onTierChange synchronously, before the
  // explicit render() below - so mounting built the entire table twice for one
  // paint. The first tier report is the mount's own, and the render() call after
  // it covers it; only later tier changes need to repaint.
  let tierSettled = false;
  // A pinned tier replaces the width observer rather than fighting it.
  // `mountWorkspaceTier` WRITES host.dataset.workspaceTier from the measured
  // width, so it is an output, not an input - setting the attribute by hand is
  // overwritten on the first resize, and `[data-workspace-tier="mobile"]` in
  // adaptive-workspace.css is what hides the table and shows the card list, so
  // losing that race empties the pane rather than just narrowing it.
  //
  // Only the pairing split uses this (Alan, 2026-08-16). Its panes are half a
  // screen wide, which measures as `mobile`, and a six-field card list is not a
  // thing you can pair from. They are deliberately narrow AND deliberately
  // tables, which is a combination the width alone cannot express.
  const tierController = forceTier
    ? (host.dataset.workspaceTier = forceTier, Object.freeze({ destroy() {} }))
    : mountWorkspaceTier({
      element: host,
      ResizeObserverClass,
      onTierChange: () => {
        if (!tierSettled) return;
        render();
      },
    });
  tierSettled = true;

  function currentTier() {
    return forceTier || host.dataset.workspaceTier || workspaceTier(host.getBoundingClientRect().width);
  }

  function allColumns() {
    return authorizedColumns(state.role);
  }

  /**
   * An explicit, caller-chosen column set — for a table mounted somewhere too
   * narrow for all thirty-nine of them (Alan, 2026-08-16: the pairing split).
   *
   * `buildColumnView` already takes a `preferences.visible` list and already
   * ignores it above the mobile tier, where it hands back every authorized
   * column. Widening that would change what every existing caller gets, so this
   * is opt-in and applies on top: pass nothing and the table is exactly what it
   * was.
   *
   * It narrows only what is DRAWN. `allColumns()` stays complete, because it is
   * how a cell editor and the note popover look their column up by key, and a
   * lookup that fails is a dead control rather than a hidden one.
   *
   * `container_no` is always kept. It is the row's identity, it carries the
   * status rail and the copy affordance, and mounting without it already throws.
   */
  function narrowToRequestedColumns(columns) {
    if (!requestedColumnKeys) return columns;
    // In the CALLER's order, not the registry's. The registry order is the
    // Dashboard's reading order and puts the pairing fields far to the right; a
    // pane that is meant to be scrolled sideways for the rest has to lead with
    // what the decision needs (Alan, 2026-08-16: "需要显示更多信息可以左右移动看").
    const byKey = new Map(columns.map((column) => [column.key, column]));
    const identity = byKey.get('container_no');
    const chosen = requestedColumnKeys
      .filter((key) => key !== 'container_no')
      .map((key) => byKey.get(key))
      .filter(Boolean);
    return identity ? [identity, ...chosen] : chosen;
  }

  /**
   * The address catalog, which may not have arrived yet.
   *
   * It is accepted as a value or as a getter (2026-08-11). Building it costs one
   * callable per customer - about twenty round trips - and the dashboard used to
   * await all of them before it painted a single row. It is only ever READ when
   * an address cell renders its custom chip or when an address editor opens, so
   * it does not have to exist at mount; passing a getter lets first paint happen
   * immediately and the dropdown fill in behind it.
   */
  function resolveAddressCatalog() {
    return typeof addressCatalog === 'function' ? addressCatalog() : addressCatalog;
  }

  function currentLocale() {
    return host.ownerDocument?.documentElement?.lang || 'en';
  }

  /**
   * A column's width. Both Note columns are as wide as the longest note they hold (Alan,
   * 2026-10-01: "note要全部都能露出来不要隐藏"), because a note was one clipped line with
   * "…" and only its first words showed. It stays one line: the windowing above prices every
   * row at one height, so wrapping would put the scroll position out. Capped, so a single
   * pasted paragraph cannot make the table a screen wide - past the cap the cell still opens
   * in full on hover or focus.
   */
  function columnWidthsFor(column, locale) {
    const normal = columnWidthsForLocale(column, locale);
    if (state.selectMode && column.key === 'container_no') {
      return {
        minWidth: (normal.minWidth ?? 0) + SELECT_BOX_PX,
        preferredWidth: (normal.preferredWidth ?? 0) + SELECT_BOX_PX,
      };
    }
    if (!NOTE_COLUMN_KEYS.has(column.key)) return normal;
    const fitted = Math.min(noteColumnCapPx(column.key), noteColumnFitPx(state.rows, column.key));
    return {
      minWidth: Math.max(normal.minWidth ?? 0, fitted),
      preferredWidth: Math.max(normal.preferredWidth ?? 0, fitted),
    };
  }

  /**
   * The column's width. It is the same while an editor is open: only the edited cell opens up
   * (see beginCellEdit), so a repaint that scrolls fresh rows in draws them at the normal width.
   */
  function editingWidthsFor(column, locale) {
    return columnWidthsFor(column, locale);
  }

  function editorWidthFor(column) {
    return EDITOR_WIDTHS[column.editor] || EDITOR_WIDTHS.text;
  }

  function tableElement() {
    return host.querySelector('.adaptive-container-table table');
  }

  /**
   * Redraw and mark the cells the pairing is about to write.
   *
   * setPairingSelection repaints in place rather than re-rendering, because the
   * operator is mid-choice and the table must not move under them. The preview
   * follows the same rule: it touches only the marked cells and clears whichever
   * ones were marked before.
   *
   * It used to be one cell - Empty # - and it worked by reaching across the table
   * for the picked empty's row. That cannot work in the split, where the two
   * Containers are in two different tables. So the caller now names the fields and
   * supplies the values on the row itself, and this marks them (Alan, 2026-08-16:
   * "一个柜子的element改一下…另外一个柜子的哪个部分也会改动并由标记…实时同步").
   */
  function repaintPreviewCells() {
    const table = tableElement();
    if (!table) return;
    const previewFields = state.pairingPreview.fields;
    const marked = previewFields instanceof Set ? previewFields : new Set();
    for (const tr of table.querySelectorAll('[data-adaptive-row]')) {
      const row = rowForId(tr.dataset.adaptiveRow);
      if (!row) continue;
      const onThisRow = row.id === state.pairingPreview.rowId;
      for (const cell of tr.querySelectorAll('[data-column-key]')) {
        const key = cell.dataset.columnKey;
        const previewed = onThisRow && marked.has(key);
        const wasPreviewed = cell.dataset.pairingPreview === 'true';
        if (!previewed && !wasPreviewed) continue;
        const column = authorizedColumns(state.role).find((candidate) => candidate.key === key);
        if (!column) continue;
        cell.innerHTML = cellModeMarkup(column, row);
        if (previewed) cell.dataset.pairingPreview = 'true';
        else delete cell.dataset.pairingPreview;
      }
    }
  }

  /**
   * Widen the column in place and hold the row lit while an editor is open.
   *
   * The widening is written straight onto the cells rather than through a
   * repaint, because render() closes the editor as its first act.
   */
  function beginCellEdit(row, column) {
    endCellEdit();
    const table = tableElement();
    if (!table) return null;
    const locale = currentLocale();
    const width = Math.max(
      columnWidthsFor(column, locale).preferredWidth,
      editorWidthFor(column),
    );
    editingCell = { key: column.key, rowId: row.id, width };
    const selector = `[data-column-key="${cssEscape(column.key)}"]`;
    const tr = table.querySelector(`[data-adaptive-row="${cssEscape(row.id)}"]`);
    tr?.setAttribute('data-row-editing', 'true');
    const cell = tr?.querySelector(selector) || null;
    // Only THIS cell is opened up, not the whole column (Alan, 2026-10-10: clicking Chassis # in
    // edit mode "突然的这种一大片白色遮挡"). Every cell and the heading of the column used to be
    // restyled at once - lifted to z-index 20, overflow shown, position changed - and the heading
    // lost its sticky position with it, which left a blank white band down the column. The
    // fixed table layout never gave the column the extra width anyway; the box simply overflows
    // its own cell, as a popover would, and that needs one cell, not a column.
    if (cell) {
      cell.style.setProperty('--editor-width', `${width}px`);
      cell.setAttribute('data-column-editing', 'true');
    }
    // The date and address cells have no close event of their own - they are
    // native controls sitting in the cell - so focus leaving is what puts the
    // column back. Harmless for the other two paths, which end explicitly.
    cell?.addEventListener('focusout', () => {
      globalThis.setTimeout(() => {
        if (editingCell?.key === column.key && !cell.contains(host.ownerDocument.activeElement)) {
          endCellEdit();
        }
      }, 0);
    });
    return cell;
  }

  function endCellEdit() {
    if (!editingCell) return;
    const table = tableElement();
    editingCell = null;
    if (!table) return;
    for (const cell of table.querySelectorAll('[data-column-editing="true"]')) {
      cell.style.removeProperty('--editor-width');
      cell.removeAttribute('data-column-editing');
    }
    for (const tr of table.querySelectorAll('[data-row-editing="true"]')) {
      tr.removeAttribute('data-row-editing');
    }
  }

  // CSS.escape is missing in the jsdom the unit tests run in, and a Container id
  // is safe enough that the fallback only has to survive quotes.
  function cssEscape(value) {
    const raw = String(value ?? '');
    return typeof CSS?.escape === 'function' ? CSS.escape(raw) : raw.replace(/["\\]/gu, '\\$&');
  }

  function cellModeMarkup(column, row) {
    const editable = state.editMode && columnEditableForRow(column, state.role, row);
    if (column.key === 'reported_empty_at') {
      const mayEdit = columnEditableForRole(column, state.role);
      return reportedEmptyCellMarkup({
        row,
        // Reporting is available in the ordinary table; only undoing it is gated on
        // Edit Mode (Alan, 2026-08-02).
        canReport: mayEdit,
        canCancel: mayEdit,
        anyStatus: mayEdit && state.role !== 'customer',
        display: dateCellDisplayText(column, row, formatCell),
        place: typeof row.reported_empty_place === 'string' ? row.reported_empty_place.trim() : '',
      });
    }
    if (column.editor === 'date') {
      return dateCellMarkup({
        column, row, editable, display: dateCellDisplayText(column, row, formatCell),
      });
    }
    if (column.editor === 'address') {
      const locale = currentLocale();
      // Same deferral as the date cell. An editable address cell used to carry a
      // <select> holding the customer's whole approved catalog, in every row; the
      // list is built when the cell is opened instead.
      const cell = addressCellMarkup({
        column,
        row,
        catalog: resolveAddressCatalog(),
        locale,
        trigger: dashboardCellContent({
          column, row, role: state.role, formatted: formatCell(column, row), editMode: false,
        }),
      });
      if (!editable) return cell;
      return `<span class="dashboard-address-open" data-open-address-editor data-container-id="${escapeHtml(row.id)}" data-field="${escapeHtml(column.key)}">${cell}</span>`;
    }
    // The pairing shows its empty number the moment the empty is picked, not
    // when it is released (Alan, 2026-08-11). Nothing is written: the number is
    // already in another row on screen, and this only draws it against the laden
    // one so the pair is legible before anybody dispatches it. Cancelling clears
    // the preview and no Container was ever touched.
    const formatted = column.key === 'empty_container_no'
      && row.id === state.pairingPreview.rowId && state.pairingPreview.containerNo
      ? state.pairingPreview.containerNo
      : isDateColumn(column)
        ? dateCellDisplayText(column, row, formatCell)
        : formatCell(column, row);
    return dashboardCellContent({
      column,
      row,
      role: state.role,
      formatted,
      editMode: state.editMode,
    });
  }

  /**
   * Swap every editable cell between its read and edit renderings.
   *
   * Clicking Edit used to take about two seconds (Alan, 2026-08-08). 452 rows of
   * some thirty editable columns is around thirteen thousand cells, rewritten 120
   * to an animation frame, and the button stayed disabled and the table stayed
   * unchanged until the last one landed. Nothing about the wait was visible
   * either - the is-edit-mode class was applied at the end, so for two seconds
   * the screen said nothing had happened.
   *
   * Three changes, none of which make the work smaller:
   *
   *   - the class flips first, so the table reads as editable immediately;
   *   - the cells on screen are converted in the first batch, because those are
   *     the only ones anybody can click in the next second;
   *   - the promise resolves once those are done rather than once all of them
   *     are, so the button comes back straight away while the rest fill in.
   */
  // The Work Pool narrows the table to the exact Containers this role must act on.
  // It filters the already-loaded rows so the query path stays untouched.
  function filteredTableRows() {
    if (!state.rowIdFilter) return state.rows;
    return state.rows.filter((row) => state.rowIdFilter.has(row.id));
  }

  function render() {

    overlayManager.close('render');
    // The widened column belongs to a cell that is about to stop existing.
    endCellEdit();
    cleanupScrollbars();
    nearEndObserver?.disconnect();
    nearEndObserver = null;
    nearEndScrollCleanup();
    nearEndScrollCleanup = () => {};
    nearEndCheck = () => {};
    if (state.destroyed) return;
    // Both axes, not just the horizontal one. The table scrolls inside itself,
    // and a render rebuilds its rows - so every repaint sent the operator back to
    // the top of 452 containers (Alan, 2026-08-08: "不是load完回到最上方而是接着
    // 下方scroll的进程继续往下"). scrollLeft has been restored here for a while;
    // scrollTop was the one nobody had missed, because the old 20-row pages never
    // put anybody far enough down to notice.
    const previousScroll = host.querySelector('.adaptive-container-table');
    const previousScrollLeft = previousScroll?.scrollLeft || 0;
    const previousScrollTop = previousScroll?.scrollTop || 0;
    const focusedContainerId = host.ownerDocument?.activeElement
      ?.closest?.('[data-adaptive-row]')?.dataset?.adaptiveRow || null;
    const tier = currentTier();
    const view = buildColumnView({ role: state.role, tier });
    // The <col> tracks and the table's own width read these, so a Note column is widened here too,
    // not only in the cell styles (see columnWidthsFor).
    const visibleColumns = orderDashboardColumns(narrowToRequestedColumns(view.visible), state.pinnedKeys)
      .map((column) => {
        if (state.selectMode && column.key === 'container_no') {
          return {
            ...column,
            minWidth: (column.minWidth ?? 0) + SELECT_BOX_PX,
            preferredWidth: (column.preferredWidth ?? 0) + SELECT_BOX_PX,
          };
        }
        if (!NOTE_COLUMN_KEYS.has(column.key)) return column;
        const fitted = Math.min(noteColumnCapPx(column.key), noteColumnFitPx(state.rows, column.key));
        return fitted > (column.preferredWidth ?? 0)
          ? { ...column, minWidth: Math.max(column.minWidth ?? 0, fitted), preferredWidth: fitted } : column;
      });
    const pinnedKeys = ['container_no', ...state.pinnedKeys]
      .filter((key) => visibleColumns.some((column) => column.key === key));
    const pinnedKeySet = new Set(pinnedKeys);
    const headerLocale = currentLocale();
    const pinnedOffsets = dashboardPinnedColumnOffsets(
      visibleColumns,
      state.pinnedKeys,
      (column) => columnWidthsFor(column, headerLocale).preferredWidth,
    );
    const lastPinnedKey = pinnedKeys.at(-1) || 'container_no';
    // The window painter builds rows without re-running the header pass, so it
    // needs the same pinning facts this render resolved.
    columnPinKeys = pinnedKeySet;
    columnPinOffsets = pinnedOffsets;
    lastPinnedColumnKey = lastPinnedKey;
    // Width is resolved per render, not read off the frozen column: a date is
    // 24px narrower in Chinese than in English and the locale changes while the
    // table is on screen (Alan, 2026-08-11: "way too big gap"). headerLocale is
    // resolved above because the pin offsets have to add up the same widths.
    const cellStyle = (column) => {
      const { minWidth, preferredWidth } = editingWidthsFor(column, headerLocale);
      return `--column-min:${minWidth}px;--column-preferred:${preferredWidth}px${pinnedOffsets.has(column.key) ? `;--column-pin-left:${pinnedOffsets.get(column.key)}px` : ''}`;
    };
    const pinAttributes = (column) => pinnedKeySet.has(column.key)
      ? ` data-column-pinned="true"${column.key === lastPinnedKey ? ' data-column-pin-last="true"' : ''}`
      : '';
    // Identifier columns are exempt from the truncation the density pass allows
    // everywhere else: a half-shown Container or MBL number is worse than a wide
    // column, so they are marked and never ellipsised.
    const identifierAttribute = (column) => (column.identifier ? ' data-column-identifier="true"' : '');
    // No pin button. Clicking the column name pins it, once the table is
    // unlocked (Alan, 2026-08-09) - a 16px control repeated across thirty-five
    // headings was a whole column's width spent on an action taken twice a week.
    const pinnable = (column) => tier !== 'mobile' && column.key !== 'container_no';
    const columnTracks = visibleColumns.map((column) => `<col data-column-key="${escapeHtml(column.key)}" style="width:${column.preferredWidth}px">`).join('');
    const tableWidth = visibleColumns.reduce((total, column) => total + column.preferredWidth, 0);
    const head = visibleColumns.map((column) => {
      const sort = dateSortHeaderState(column, state.sortDateKey, { ascending: state.sortAscending });
      const ariaSort = sort.sortable ? ` aria-sort="${sort.ariaSort}"` : '';
      // The header is the label and nothing else (Alan, 2026-08-10). The grey
      // "editable" dot and the sort icon are both gone: the dot was added when
      // the Edit toggle was removed, and the toggle is back, and neither was
      // asked for. Between them they cost 4px + 16px + two gaps on every one of
      // thirty-odd headings.
      const writable = columnEditableForRole(column, state.role);
      // The heading carries BOTH actions and the handler picks by lock state.
      // It cannot be chosen here: setLocked only toggles a class - no repaint -
      // so markup that branched on state.locked would still describe the mode
      // the table was last drawn in.
      const sortable = dateSortHeaderState(column, state.sortDateKey).sortable;
      const activates = sortable || pinnable(column);
      const hint = sortable && pinnable(column)
        ? ' — 点击排序 click to sort · 解锁后固定 unlock to pin'
        : sortable ? ' — 点击排序 click to sort'
          : pinnable(column) ? ' — 解锁后点击固定 unlock to pin' : '';
      return `<th data-column-key="${column.key}" data-column-editable="${columnUsesEditSurface(column, state.role)}"${writable ? ' data-column-writable="true"' : ''}${ariaSort}${pinAttributes(column)}${identifierAttribute(column)} style="${cellStyle(column)}"><span class="dashboard-column-heading"${sortable ? ` data-sort-date-column="${escapeHtml(column.key)}"` : ''}${pinnable(column) ? ` data-toggle-column-pin="${escapeHtml(column.key)}" aria-pressed="${pinnedKeySet.has(column.key)}"` : ''}${activates ? ' role="button" tabindex="0"' : ''} aria-label="${escapeHtml(column.label)}${writable ? ' (可修改)' : ''}${hint}" title="${escapeHtml(column.label)}${hint.replace(' — ', ' · ')}"><span>${escapeHtml(column.headerLabel || column.label)}</span></span></th>`;
    }).join('');
    const filteredRows = filteredTableRows();
    const renderedRows = state.sortDateKey
      ? sortRowsByDate(filteredRows, state.sortDateKey, { ascending: state.sortAscending })
      : sortRowsBySearchOrder(sortRowsByStatusBand(filteredRows), state.searchOrder);
    // The rows on screen are about to be redrawn: keep what they measured, so the spacers stay true.
    host.querySelectorAll('.adaptive-container-table tbody tr.adaptive-task').forEach(rememberRowHeight);
    windowedRows = renderedRows;
    windowedColumns = visibleColumns;
    rowWindow = computeRowWindow(renderedRows.length);
    const body = rowWindowMarkup(renderedRows, rowWindow, visibleColumns);
    // The card list is the mobile presentation; adaptive-workspace.css hides it
    // at every other tier. Building it on a desktop render was a second full
    // walk of the row set, three formatCell calls each, and a pile of DOM that
    // is never painted. It is built only for the tier that shows it, and the
    // tier change repaints anyway, so nothing is missing when the window
    // narrows.
    const cards = currentTier() === 'mobile' ? renderedRows.map((row) => {
      const tone = containerStatusTone(row);
      const done = containerStatusBand(row) === STATUS_BAND_DONE ? ' is-status-done' : '';
      const awaiting = dispatchHighlightClass(row);
      const highlighted = state.highlightedId === row.id ? ' is-work-queue-target' : '';
      const marked = state.markedRowId === row.id ? ' data-row-marked="true"' : '';
      return `<li class="adaptive-container-card adaptive-task adaptive-task--${tone}${done}${awaiting}${highlighted}" data-container-status-tone="${tone}"${marked} data-adaptive-row="${escapeHtml(row.id)}"><button type="button" data-select-row="${escapeHtml(row.id)}"><strong>${escapeHtml(row.container_no)}</strong><span>${escapeHtml(formatCell({ key: 'status' }, row))}</span><span>${escapeHtml(formatCell({ key: 'next_action' }, row))}</span><time>${escapeHtml(formatCell({ key: 'critical_deadline' }, row))}</time></button></li>`;
    }).join('') : '';
    host.classList.toggle('is-edit-mode', state.editMode);
    host.classList.toggle('is-locked', state.locked);
    host.innerHTML = `<section class="adaptive-container-workspace">${emptyPick.isActive() ? emptyPickBannerMarkup(filteredTableRows().length) : (externalPick ? externalEmptyPickBannerMarkup() : '')}<div class="adaptive-container-table" role="region" aria-label="Containers data table" tabindex="0"><table style="width:${tableWidth}px;min-width:${tableWidth}px"><colgroup>${columnTracks}</colgroup><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div><div class="adaptive-scrollbar" role="scrollbar" aria-label="Horizontal table scroll" aria-orientation="horizontal" aria-valuemin="0" tabindex="0"><div class="adaptive-scrollbar__track"><span class="adaptive-scrollbar__thumb"></span></div></div><ol class="adaptive-container-cards">${cards}</ol></section>`;
    // Whatever cell was open went out with the old markup.
    inlineControl = null;
    // Synchronous on purpose. Deferring this by a frame did save a layout, but
    // the custom horizontal scrollbar is wired from the measurements it takes -
    // until it runs, the track has no thumb and dragging the table sideways does
    // nothing. A frame of saved layout is not worth a scrollbar that does not
    // work for that frame, and the render is not complete without it.
    syncScrollbars();
    const table = host.querySelector('.adaptive-container-table');
    if (table) {
      table.scrollLeft = previousScrollLeft;
      table.scrollTop = previousScrollTop;
    }
    // A first render starts at row zero, so the window it guessed is right; what
    // it cannot know until the rows exist is how tall one is. Correct the spacers
    // once, then let the scroll handler take over.
    if (renderedRows.length > ROW_WINDOW_MINIMUM) {
      if (measureRowHeight()) {
        const body = host.querySelector('.adaptive-container-table tbody');
        if (body) {
          rowWindow = computeRowWindow(renderedRows.length);
          body.innerHTML = rowWindowMarkup(renderedRows, rowWindow, visibleColumns);
        }
      }
      // Below the minimum the IntersectionObserver further down still asks for the
      // next page; above it the window is what knows where the operator is, and a
      // window that already reaches the tail has to ask on its own.
      if (rowWindow.end >= renderedRows.length - 10) onNearEnd(Math.max(0, renderedRows.length - 10));
      // Widen the margin on the next frame rather than in this one: the paint the
      // operator is waiting for stays small, and the headroom a flick needs is
      // there before they can reach the wheel.
      host.ownerDocument?.defaultView?.requestAnimationFrame?.(() => {
        if (state.destroyed) return;
        rowWindowBuffer = ROW_WINDOW_SETTLED_BUFFER;
        paintRowWindow();
      });
    }
    if (focusedContainerId) {
      const focusedRow = [...host.querySelectorAll('[data-adaptive-row]')]
        .find((candidate) => candidate.dataset.adaptiveRow === focusedContainerId);
      focusedRow?.focus?.({ preventScroll: true });
    }
    if (typeof IntersectionObserverClass === 'function' && renderedRows.length > 0) {
      const anchorIndex = Math.max(0, renderedRows.length - 10);
      const anchorSelector = currentTier() === 'mobile'
        ? '.adaptive-container-card[data-adaptive-row]'
        : 'tr[data-adaptive-row]';
      const anchorRows = host.querySelectorAll(anchorSelector);
      const anchorRow = anchorRows[anchorIndex];
      const lastRow = anchorRows[anchorRows.length - 1];
      if (anchorRow) {
        // Pagination used to stop dead in Edit Mode, so scrolling to the bottom of
        // the grid loaded nothing and the rest of the Containers were unreachable
        // without leaving Edit Mode first (Alan, 2026-08-01). Nothing needed that
        // guard: render() reapplies is-edit-mode and cellModeMarkup already builds
        // appended rows in the current mode, and setRows defers its re-render while
        // a cell editor overlay is open, so an in-flight edit is not clobbered.
        const notifyNearEnd = () => {
          onNearEnd(anchorIndex);
        };
        nearEndObserver = new IntersectionObserverClass((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) notifyNearEnd();
        }, { root: null, threshold: 0.01 });
        nearEndObserver.observe(anchorRow);
        if (lastRow && lastRow !== anchorRow) nearEndObserver.observe(lastRow);
        const viewWindow = host.ownerDocument?.defaultView;
        const viewDocument = host.ownerDocument;
        if (viewWindow?.addEventListener && viewWindow?.removeEventListener) {
          let frame = null;
          const check = () => {
            frame = null;
            const anchorVisible = nearEndAnchorVisible(anchorRow.getBoundingClientRect(), viewWindow.innerHeight);
            if (anchorVisible) {
              notifyNearEnd();
            }
          };
          const schedule = () => {
            if (frame !== null) return;
            frame = viewWindow.requestAnimationFrame(check);
          };
          viewWindow.addEventListener('scroll', schedule, { passive: true });
          viewWindow.addEventListener('wheel', schedule, { passive: true });
          viewDocument?.addEventListener('scroll', schedule, { capture: true, passive: true });
          table?.addEventListener('scroll', schedule, { passive: true });
          nearEndCheck = schedule;
          schedule();
          nearEndScrollCleanup = () => {
            viewWindow.removeEventListener('scroll', schedule);
            viewWindow.removeEventListener('wheel', schedule);
            viewDocument?.removeEventListener('scroll', schedule, true);
            table?.removeEventListener('scroll', schedule);
            if (frame !== null) viewWindow.cancelAnimationFrame(frame);
            frame = null;
          };
        }
      }
    }
  }

  const copyKey = (row) => normalizedCopyKey(row?.container_no);

  function syncCopiedMarks() {
    for (const element of host.querySelectorAll('tr[data-adaptive-row]')) {
      const row = rowForId(element.dataset.adaptiveRow);
      if (row && state.copiedNumbers.has(copyKey(row))) element.dataset.copied = 'true';
      else delete element.dataset.copied;
      if (row && state.previewNumbers.has(copyKey(row))) element.dataset.copyPreview = 'true';
      else delete element.dataset.copyPreview;
    }
  }

  // Dispatchers only (Alan, 2026-08-21: "在表格上highlight只给dispatcher看"). The
  // mark is a dispatcher's own working note about what is already out; it is not
  // a status the other desks act on, and it would only add colour to their table.
  // One function because the row and the mobile card both draw it.
  function dispatchHighlightClass(row) {
    if (state.role !== 'dispatcher') return '';
    return rowAwaitsDriver(row, state.activeDispatchIds) ? ' is-awaiting-driver' : '';
  }

  function tableRowMarkup(row, visibleColumns) {
    const tone = containerStatusTone(row);
    const done = containerStatusBand(row) === STATUS_BAND_DONE ? ' is-status-done' : '';
    const awaiting = dispatchHighlightClass(row);
    const highlighted = state.highlightedId === row.id ? ' is-work-queue-target' : '';
    const pick = row.id === state.pairingPick.ladenId ? 'laden'
      : row.id === state.pairingPick.emptyId ? 'empty' : '';
    // Copying a group is a lookup you then go and perform on the terminal's site,
    // so the rows you took stay marked until you clear them (Alan, 2026-08-09).
    const copied = `${state.copiedNumbers.has(copyKey(row)) ? ' data-copied="true"' : ''}${state.previewNumbers.has(copyKey(row)) ? ' data-copy-preview="true"' : ''}`;
    // Must resolve the same way the header pass did, or the body columns would
    // sit at English widths under a Chinese header row.
    const rowLocale = currentLocale();
    const cellStyle = (column) => {
      const { minWidth, preferredWidth } = editingWidthsFor(column, rowLocale);
      return `--column-min:${minWidth}px;--column-preferred:${preferredWidth}px${columnPinOffsets.has(column.key) ? `;--column-pin-left:${columnPinOffsets.get(column.key)}px` : ''}`;
    };
    const pinAttributes = (column) => (columnPinKeys.has(column.key)
      ? ` data-column-pinned="true"${column.key === lastPinnedColumnKey ? ' data-column-pin-last="true"' : ''}`
      : '');
    const marked = state.markedRowId === row.id ? ' data-row-marked="true"' : '';
    const ticked = state.selectMode && state.selectedIds.has(row.id) ? ' data-row-selected="true"' : '';
    return `<tr class="adaptive-task adaptive-task--${tone}${done}${awaiting}${highlighted}" data-container-status-tone="${tone}"${pick ? ` data-pairing-pick="${pick}" aria-selected="true"` : ''}${copied}${marked}${ticked} data-adaptive-row="${escapeHtml(row.id)}" tabindex="0" aria-label="Container ${escapeHtml(row.container_no)}">${visibleColumns.map((column, index) => `<td data-column-key="${column.key}" data-column-editable="${columnUsesEditSurface(column, state.role)}"${pinAttributes(column)}${cellMarkAttribute(row, column)} style="${cellStyle(column)}">${index === 0 ? `${selectBoxMarkup(row)}<button type="button" class="adaptive-task-status-rail" data-open-inspector aria-label="Open read-only information for ${escapeHtml(row.container_no)}"></button>` : ''}${cellModeMarkup(column, row)}</td>`).join('')}</tr>`;
  }

  function selectBoxMarkup(row) {
    if (!state.selectMode) return '';
    return `<input type="checkbox" class="adaptive-row-check" data-select-row="${escapeHtml(row.id)}"${state.selectedIds.has(row.id) ? ' checked' : ''} aria-label="Select ${escapeHtml(row.container_no ?? '')}">`;
  }

  // The ticked rows that are on screen under the current filter - never a row the filter hides, so a
  // bulk change cannot reach something the operator cannot see.
  function selectedVisibleRows() {
    return windowedRows.filter((row) => state.selectedIds.has(row.id));
  }

  function announceSelection() {
    const view = host.ownerDocument?.defaultView;
    const EventClass = view?.CustomEvent ?? globalThis.CustomEvent;
    if (typeof EventClass !== 'function') return;
    host.dispatchEvent(new EventClass('adaptive-selection', {
      bubbles: true, detail: { count: selectedVisibleRows().length, total: windowedRows.length },
    }));
  }

  function paintSelection() {
    host.querySelectorAll('tr[data-adaptive-row]').forEach((tr) => {
      const on = state.selectMode && state.selectedIds.has(tr.dataset.adaptiveRow);
      if (on) tr.setAttribute('data-row-selected', 'true');
      else tr.removeAttribute('data-row-selected');
      const box = tr.querySelector('[data-select-row]');
      if (box) box.checked = on;
    });
    announceSelection();
  }

  // Capture phase, ahead of the row's own handlers: a click on the box ticks it and does nothing else.
  // Shift-click ticks everything between this row and the last one ticked, in the order drawn.
  function onSelectBoxClick(event) {
    const box = event.target.closest?.('[data-select-row]');
    if (!box) return;
    event.stopImmediatePropagation();
    const id = box.dataset.selectRow;
    const turnOn = box.checked;
    if (event.shiftKey && state.lastSelectedId && state.lastSelectedId !== id) {
      const order = windowedRows.map((row) => row.id);
      const from = order.indexOf(state.lastSelectedId);
      const to = order.indexOf(id);
      if (from !== -1 && to !== -1) {
        for (const between of order.slice(Math.min(from, to), Math.max(from, to) + 1)) {
          if (turnOn) state.selectedIds.add(between);
          else state.selectedIds.delete(between);
        }
      }
    } else if (turnOn) {
      state.selectedIds.add(id);
    } else {
      state.selectedIds.delete(id);
    }
    state.lastSelectedId = id;
    paintSelection();
  }

  function cellMarkAttribute(row, column) {
    const color = state.cellMarks.get(`${row?.id}|${column.key}`);
    return color ? ` data-cell-mark="${color}"` : '';
  }

  // The rows above and below the window are one spacer row each, holding exactly
  // the height the real rows would have. The document keeps its length, so the
  // scrollbar, the scroll position and scrollIntoView all behave as if every row
  // were there.
  function rowWindowMarkup(rows, window_, visibleColumns) {
    const columns = Math.max(1, visibleColumns.length);
    // An empty table says WHAT is narrowing it (Alan, 2026-08-19). He searched a
    // Container number that exists, got "No Containers match this view", and had
    // no way to tell a mistyped digit from a filter still switched on from the
    // system having lost the row - which is a question he has now had to ask three
    // times in one day. The sentence names the filters that are actually on.
    if (!rows.length) {
      return `<tr><td colspan="${columns}">${escapeHtml(state.emptyReason || 'No Containers match this view.')}</td></tr>`;
    }
    // Both spacers are always present, even at zero height. They are the anchors
    // the incremental repaint splices rows against, so they must not come and go.
    const spacer = (position, px) => `<tr class="adaptive-row-spacer" data-spacer="${position}" aria-hidden="true"><td colspan="${columns}" style="height:${Math.max(0, px)}px"></td></tr>`;
    return spacer('top', rowsHeightPx(rows, 0, window_.start))
      + rows.slice(window_.start, window_.end).map((row) => tableRowMarkup(row, visibleColumns)).join('')
      + spacer('bottom', rowsHeightPx(rows, window_.end, rows.length));
  }

  function computeRowWindow(total) {
    const view = host.ownerDocument?.defaultView;
    if (!view || total <= ROW_WINDOW_MINIMUM) {
      return rowWindowBounds({ total, firstVisible: 0, visibleCount: total });
    }
    // Whichever box the rows actually scroll in: the table's own, when the
    // Dashboard gives it a fixed height, and the page otherwise. One formula
    // covers both, because both are measured in viewport coordinates.
    const table = host.querySelector('.adaptive-container-table');
    const scrolls = !!table && table.scrollHeight > table.clientHeight + 1;
    // A hidden tab reports a viewport of zero. Falling through to "one row" there
    // would leave the table nearly empty the moment it came back.
    const viewportTop = scrolls ? table.getBoundingClientRect().top : 0;
    const viewportHeight = (scrolls ? table.clientHeight : view.innerHeight) || 900;
    // The tbody's top is row zero's position whatever is rendered, because the
    // spacers preserve it. On the first render there is no tbody yet and the
    // table starts at the top of its own scroll, which is the same answer.
    const body = host.querySelector('.adaptive-container-table tbody');
    const bodyTop = body ? body.getBoundingClientRect().top : viewportTop;
    return rowWindowBounds({
      total,
      firstVisible: rowIndexAtOffset(windowedRows, Math.max(0, viewportTop - bodyTop)),
      visibleCount: viewportHeight / rowHeightPx,
      buffer: rowWindowBuffer,
    });
  }

  /**
   * Repaints the rows alone, and only the rows that actually changed.
   *
   * Rebuilding the whole window on every move is what left blank bands under a
   * fast scroll (Alan, 2026-08-09: "往下面scroll的时候会看见空白的"): a hundred rows
   * is tens of milliseconds, far past a frame, so the spacers had already moved
   * while the rows had not. Scrolling a screen moves the window by a handful of
   * rows, so those rows are spliced in against the two spacers and the rest of
   * the DOM is left alone - a few hundred microseconds, inside the frame.
   *
   * Nothing outside the tbody is touched, so the header, the pinned columns, the
   * horizontal scroll position and any open overlay survive a scroll.
   */
  function paintRowWindow() {
    if (state.destroyed || !windowedRows.length) return;
    const body = host.querySelector('.adaptive-container-table tbody');
    if (!body) return;
    const next = computeRowWindow(windowedRows.length);
    if (next.start === rowWindow.start && next.end === rowWindow.end) return;
    const top = body.querySelector('tr[data-spacer="top"]');
    const bottom = body.querySelector('tr[data-spacer="bottom"]');
    // A jump with nothing in common - a scrollbar drag, a locate - is cheaper
    // rebuilt than spliced.
    if (!top || !bottom || next.start >= rowWindow.end || next.end <= rowWindow.start) {
      body.querySelectorAll('tr.adaptive-task').forEach(rememberRowHeight);
      rowWindow = next;
      body.innerHTML = rowWindowMarkup(windowedRows, rowWindow, windowedColumns);
    } else {
      while (rowWindow.start < next.start) {
        const first = top.nextElementSibling;
        if (!first || first === bottom) break;
        rememberRowHeight(first);
        first.remove();
        rowWindow.start += 1;
      }
      while (rowWindow.start > next.start) {
        rowWindow.start -= 1;
        top.insertAdjacentHTML('afterend', tableRowMarkup(windowedRows[rowWindow.start], windowedColumns));
      }
      while (rowWindow.end > next.end) {
        const last = bottom.previousElementSibling;
        if (!last || last === top) break;
        rememberRowHeight(last);
        last.remove();
        rowWindow.end -= 1;
      }
      while (rowWindow.end < next.end) {
        bottom.insertAdjacentHTML('beforebegin', tableRowMarkup(windowedRows[rowWindow.end], windowedColumns));
        rowWindow.end += 1;
      }
      top.firstElementChild.style.height = `${rowsHeightPx(windowedRows, 0, rowWindow.start)}px`;
      bottom.firstElementChild.style.height = `${rowsHeightPx(windowedRows, rowWindow.end, windowedRows.length)}px`;
    }
    // The window is tight on the first paint so the table appears fast. Once it
    // is on screen the margin widens, because a flick can outrun a narrow one and
    // widening it now costs only the rows it adds.
    rowWindowBuffer = ROW_WINDOW_SETTLED_BUFFER;
    // Reaching the tail of what is loaded still asks for the next page.
    if (rowWindow.end >= windowedRows.length - 10) onNearEnd(Math.max(0, windowedRows.length - 10));
  }

  // One measurement of the real rows keeps the spacers honest if the density ever
  // changes; the CSS token is only the starting guess. The average, not the first
  // row: a wrapped Note makes some rows two or three lines tall (2026-10-12), and
  // the spacers stand in for rows of every height.
  function measureRowHeight() {
    const rendered = [...host.querySelectorAll('.adaptive-container-table tbody tr.adaptive-task')];
    const total = rendered.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0);
    const measured = rendered.length ? Math.round(total / rendered.length) : 0;
    if (measured <= 0 || measured === rowHeightPx) return false;
    rowHeightPx = measured;
    return true;
  }

  function cleanupScrollbars() {
    scrollbarCleanup();
    scrollbarCleanup = () => {};
  }

  function syncScrollbars() {
    const table = host.querySelector('.adaptive-container-table');
    const rail = host.querySelector('.adaptive-scrollbar');
    const track = rail?.querySelector('.adaptive-scrollbar__track');
    const thumb = rail?.querySelector('.adaptive-scrollbar__thumb');
    if (!table || !rail || !track || !thumb) return;
    // Read at pointerdown, not captured here: setLocked only toggles a class, so
    // a boolean baked in at mount would describe the mode the table was drawn in.
    const cleanupTablePan = mountTableDragPan(table, { locked: () => state.locked });
    let drag = null;
    const metrics = () => scrollbarThumbMetrics({
      clientWidth: table.clientWidth,
      trackWidth: track.clientWidth,
      scrollWidth: table.scrollWidth,
      scrollLeft: table.scrollLeft,
    });
    const syncThumb = () => {
      const value = metrics();
      thumb.style.width = `${value.thumbWidth}px`;
      thumb.style.transform = `translateX(${value.thumbLeft}px)`;
      rail.setAttribute('aria-valuemax', String(Math.round(value.maxScroll)));
      rail.setAttribute('aria-valuenow', String(Math.round(table.scrollLeft)));
      rail.dataset.scrollable = String(value.maxScroll > 0);
    };
    const beginDrag = (event) => {
      if (event.button !== 0) return;
      drag = { pointerId: event.pointerId, startX: event.clientX, startScroll: table.scrollLeft };
      thumb.dataset.dragging = 'true';
      thumb.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };
    const moveDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const value = metrics();
      const scrollPerPixel = value.maxThumbLeft > 0
        ? value.maxScroll / value.maxThumbLeft : 0;
      table.scrollLeft = drag.startScroll + ((event.clientX - drag.startX) * scrollPerPixel);
      syncThumb();
      event.preventDefault();
    };
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      thumb.releasePointerCapture?.(event.pointerId);
      drag = null;
      delete thumb.dataset.dragging;
    };
    const jumpTrack = (event) => {
      if (event.target === thumb || event.button !== 0) return;
      const value = metrics();
      const rect = track.getBoundingClientRect();
      const nextLeft = Math.min(value.maxThumbLeft, Math.max(
        0, event.clientX - rect.left - (value.thumbWidth / 2),
      ));
      table.scrollLeft = value.maxThumbLeft > 0
        ? (nextLeft / value.maxThumbLeft) * value.maxScroll : 0;
      syncThumb();
      event.preventDefault();
    };
    const wheelRail = (event) => {
      const delta = Math.abs(event.deltaX) >= Math.abs(event.deltaY)
        ? event.deltaX : event.deltaY;
      if (!delta) return;
      table.scrollLeft += delta;
      syncThumb();
      event.preventDefault();
    };
    const keyRail = (event) => {
      const value = metrics();
      const next = {
        ArrowLeft: table.scrollLeft - 120,
        ArrowRight: table.scrollLeft + 120,
        Home: 0,
        End: value.maxScroll,
      }[event.key];
      if (next === undefined) return;
      table.scrollLeft = next;
      syncThumb();
      event.preventDefault();
    };
    const resizeObserver = typeof ResizeObserverClass === 'function'
      ? new ResizeObserverClass(syncThumb) : null;
    table.addEventListener('scroll', syncThumb, { passive: true });
    track.addEventListener('pointerdown', jumpTrack);
    thumb.addEventListener('pointerdown', beginDrag);
    thumb.addEventListener('pointermove', moveDrag);
    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);
    rail.addEventListener('wheel', wheelRail, { passive: false });
    rail.addEventListener('keydown', keyRail);
    resizeObserver?.observe?.(table);
    resizeObserver?.observe?.(track);
    syncThumb();
    scrollbarCleanup = () => {
      table.removeEventListener('scroll', syncThumb);
      track.removeEventListener('pointerdown', jumpTrack);
      thumb.removeEventListener('pointerdown', beginDrag);
      thumb.removeEventListener('pointermove', moveDrag);
      thumb.removeEventListener('pointerup', endDrag);
      thumb.removeEventListener('pointercancel', endDrag);
      rail.removeEventListener('wheel', wheelRail);
      rail.removeEventListener('keydown', keyRail);
      resizeObserver?.disconnect?.();
      cleanupTablePan();
    };
  }

  function rowForId(id) {
    return state.rows.find((row) => row.id === id) || null;
  }

  // Reporting and un-reporting both write one field; the callable derives the
  // Ocean Status from it, because a customer may write reported_empty_at but not
  // ocean_status and a two-write client would fail for them (Alan, 2026-08-02).
  function saveReportedEmpty(row, value) {
    return Promise.resolve(onSaveField({ containerId: row.id, field: 'reported_empty_at', value }))
      .then(() => true)
      .catch((error) => {
        onEditorError(error, { containerId: row.id, field: 'reported_empty_at' });
        return false;
      });
  }

  // Report Empty for staff: one box, typed the way the ops sheet writes it - "9/28空611"
  // is reported 9/28, at 611 (Alan, 2026-09-29). Blank still means "now, no place".
  // The report is written first and the place second: the report is the fact, the
  // place is detail, and if the place is refused the report must not be lost with it.
  function openReportEmptyEntry(trigger, row) {
    const panel = host.ownerDocument.createElement('div');
    panel.className = 'dashboard-floating-options dashboard-report-empty-entry';
    panel.innerHTML = `<label class="dashboard-report-empty-entry__label"><span>报空 Report Empty · 日期空地点 date 空 place</span>
      <input type="text" data-report-empty-input placeholder="9/28空611" autocomplete="off" spellcheck="false" aria-label="Report Empty date and place"></label>
      <p class="dashboard-report-empty-entry__error" data-report-empty-error role="alert"></p>`;
    overlayManager.open({ anchor: trigger, content: panel, kind: 'cell-options' });
    const input = panel.querySelector('[data-report-empty-input]');
    const error = panel.querySelector('[data-report-empty-error]');
    let saving = false;
    input?.addEventListener('keydown', async (event) => {
      if (event.key !== 'Enter' || saving) return;
      event.preventDefault();
      const entry = parseReportedEmptyEntry(input.value);
      if (entry.error) {
        error.textContent = '日期看不懂 — not a date. Try 9/28空611.';
        return;
      }
      saving = true;
      input.disabled = true;
      const saved = await saveReportedEmpty(row, entry.reportedAt);
      if (!saved) {
        saving = false;
        input.disabled = false;
        return;
      }
      // The place goes to the report's own field, never to Empty From. A report with no place also
      // clears one left by an earlier report, so the cell never shows a stale "611".
      if (entry.location || String(row.reported_empty_place ?? '').trim()) {
        const placed = await Promise.resolve(onSaveField({
          containerId: row.id, field: 'reported_empty_place', value: entry.location,
        })).then(() => true).catch((failure) => {
          onEditorError(failure, { containerId: row.id, field: 'reported_empty_place' });
          return false;
        });
        if (!placed) {
          error.textContent = '已报空，但地点没存上 — reported, but the place was not saved.';
          saving = false;
          input.disabled = false;
          return;
        }
      }
      overlayManager.close('selection');
    });
    input?.focus?.();
  }

  // Cancelling asks twice on purpose - one stray click on a date should not undo a
  // report (Alan, 2026-08-02).
  function openCancelReportEmpty(trigger, row) {
    const panel = host.ownerDocument.createElement('div');
    panel.className = 'dashboard-floating-options';
    panel.setAttribute('role', 'listbox');
    panel.innerHTML = '<button type="button" role="option" class="dashboard-floating-option" data-cancel-report-empty-confirm>Cancel Report Empty</button>';
    overlayManager.open({ anchor: trigger, content: panel, kind: 'cell-options' });
    panel.querySelector('[data-cancel-report-empty-confirm]')?.addEventListener('click', async () => {
      const saved = await saveReportedEmpty(row, null);
      // A cancelled report takes its place with it.
      if (saved && String(row.reported_empty_place ?? '').trim()) {
        await Promise.resolve(onSaveField({ containerId: row.id, field: 'reported_empty_place', value: '' })).catch(() => {});
      }
      if (saved) overlayManager.close('selection');
    });
  }

  function saveEditorValue(row, column, rawValue) {
    try {
      const value = dashboardEditorValue(column, rawValue);
      return Promise.resolve(onSaveField({ containerId: row.id, field: column.key, value }))
        .then(() => true)
        .catch((error) => {
          onEditorError(error, { containerId: row.id, field: column.key });
          return false;
        });
    } catch (error) {
      onEditorError(error, { containerId: row.id, field: column.key });
      return Promise.resolve(false);
    }
  }

  function loadCachedDriverOptions({ refresh = false } = {}) {
    if (refresh) driverOptionsPromise = null;
    if (!driverOptionsPromise) {
      driverOptionsPromise = Promise.resolve(loadDriverOptions())
        .then(normalizeDashboardDrivers)
        .catch((error) => {
          driverOptionsPromise = null;
          throw error;
        });
    }
    return driverOptionsPromise;
  }

  function bindDriverOptionKeyboard(list) {
    const buttons = [...list.querySelectorAll('[data-driver-option-name]')];
    buttons.forEach((button, index) => {
      button.addEventListener('keydown', (event) => {
        const offset = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
        if (!offset || !buttons.length) return;
        event.preventDefault();
        buttons[(index + offset + buttons.length) % buttons.length]?.focus?.();
      });
    });
  }

  function positionDriverOverlay(trigger, overlay) {
    const view = host.ownerDocument?.defaultView;
    const anchorBounds = trigger.getBoundingClientRect?.();
    const overlayBounds = overlay.getBoundingClientRect?.();
    if (!view || !anchorBounds || !overlayBounds) return;
    const safeTop = 12;
    const safeBottom = view.innerHeight - 12;
    const below = anchorBounds.bottom + 5;
    const above = anchorBounds.top - overlayBounds.height - 5;
    const top = below + overlayBounds.height <= safeBottom
      ? below
      : Math.max(safeTop, above);
    overlay.style.setProperty('--dashboard-overlay-top', `${top}px`);
  }

  // A name can be typed as well as picked (Alan, 2026-09-27: "整个流程都是直接可以
  // 手动操作的"). The list was the only way in, so a driver without an account - or
  // a moment with no drivers loaded at all - was a cell nobody could fill. The box
  // sits above the list, narrows it as you type, and Enter saves what it holds.
  function openDriverEditor(trigger, row, column) {
    const panel = host.ownerDocument.createElement('div');
    panel.className = 'dashboard-driver-editor';
    panel.innerHTML = `${dashboardDriverTypedMarkup(editorCurrentValue(row, column))}<div data-driver-list>${dashboardDriverOptionsMarkup({ state: 'loading' })}</div>`;
    const typed = panel.querySelector('[data-driver-typed]');
    const listHost = panel.querySelector('[data-driver-list]');
    let loadedDrivers = [];
    const overlay = overlayManager.open({
      anchor: trigger,
      content: panel,
      kind: 'driver-options',
      ariaLabel: `Choose ${column.label}`,
    });
    positionDriverOverlay(trigger, overlay);
    let requestGeneration = 0;
    // Narrows the list to the names holding what is typed; the Unassigned row stays.
    const filterOptions = () => {
      const needle = String(typed?.value ?? '').trim().toLocaleLowerCase();
      listHost?.querySelectorAll('[data-driver-option-name]').forEach((button) => {
        const name = String(button.dataset.driverOptionName ?? '').toLocaleLowerCase();
        button.hidden = Boolean(needle) && Boolean(name) && !name.includes(needle);
      });
    };
    let typedSaving = false;
    typed?.addEventListener('input', filterOptions);
    typed?.addEventListener('keydown', async (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        [...(listHost?.querySelectorAll('[data-driver-option-name]') ?? [])]
          .find((button) => !button.hidden)?.focus?.();
        return;
      }
      if (event.key !== 'Enter' || typedSaving) return;
      event.preventDefault();
      typedSaving = true;
      const saved = await saveEditorValue(row, column, typedDriverName(typed.value, loadedDrivers));
      typedSaving = false;
      if (saved) overlayManager.close('selection');
    });
    const renderOptions = async ({ refresh = false } = {}) => {
      const generation = ++requestGeneration;
      if (listHost) listHost.innerHTML = dashboardDriverOptionsMarkup({ state: 'loading' });
      try {
        const drivers = await loadCachedDriverOptions({ refresh });
        if (!overlay.isConnected || generation !== requestGeneration) return;
        loadedDrivers = drivers;
        if (listHost) {
          listHost.innerHTML = dashboardDriverOptionsMarkup({
            label: column.label,
            selectedName: editorCurrentValue(row, column),
            drivers,
          });
        }
        filterOptions();
        positionDriverOverlay(trigger, overlay);
        panel.querySelectorAll('[data-driver-option-name]').forEach((button) => {
          button.addEventListener('click', async () => {
            button.disabled = true;
            const saved = await saveEditorValue(row, column, button.dataset.driverOptionName);
            if (saved) overlayManager.close('selection');
            else button.disabled = false;
          });
        });
        const list = panel.querySelector('.dashboard-driver-options[role="listbox"]');
        if (list) bindDriverOptionKeyboard(list);
      } catch (error) {
        if (!overlay.isConnected || generation !== requestGeneration) return;
        if (listHost) listHost.innerHTML = dashboardDriverOptionsMarkup({ state: 'error' });
        positionDriverOverlay(trigger, overlay);
        panel.querySelector('[data-driver-retry]')?.addEventListener('click', () => {
          void renderOptions({ refresh: true });
        });
        onEditorError(error, { containerId: row.id, field: column.key });
      }
    };
    // The box has the focus from the start, so typing needs no click first.
    typed?.focus?.({ preventScroll: true });
    typed?.select?.();
    void renderOptions();
  }

  function beginEmptyPick(sourceRow) {
    if (typeof onBeginEmptyPick === 'function' && onBeginEmptyPick(sourceRow)) return;
    const started = emptyPick.begin({ sourceRow, rows: state.rows });
    if (!started) {
      onEditorError(new Error('No Container has been reported empty yet.'), {
        containerId: sourceRow.id, field: 'empty_container_no',
      });
      return;
    }
    rowIdFilterBeforePick = state.rowIdFilter;
    state.rowIdFilter = new Set(started.candidateIds);
    render();
  }

  function endEmptyPick() {
    if (externalPick) {
      const pending = externalPick;
      externalPick = null;
      render();
      pending.onCancel?.();
      return true;
    }
    if (!emptyPick.isActive()) return false;
    emptyPick.cancel();
    state.rowIdFilter = rowIdFilterBeforePick;
    rowIdFilterBeforePick = null;
    render();
    return true;
  }

  function completeEmptyPick(row) {
    const sourceRow = rowForId(emptyPick.sourceContainerId);
    const picked = emptyPick.pick(row);
    if (!picked || !sourceRow) return false;
    state.rowIdFilter = rowIdFilterBeforePick;
    rowIdFilterBeforePick = null;
    const column = authorizedColumns(state.role)
      .find(({ key }) => key === 'empty_container_no');
    if (column) void saveEditorValue(sourceRow, column, picked.value);
    render();
    return true;
  }

  // One heavy control on the page at a time.
  //
  // Date and address cells edit through a native control rather than a popover,
  // and those controls are the expensive ones - a date input carries a calendar,
  // an address select carries the customer's catalog. Opening a second cell puts
  // the first one back, and leaving a cell puts it back too, so the table holds
  // at most one of them however long the session runs.
  let inlineControl = null;

  function closeInlineControl() {
    const open = inlineControl;
    inlineControl = null;
    if (open?.node?.isConnected) open.node.outerHTML = open.restore;
  }

  function openInlineControl(trigger, build) {
    const row = rowForElement(trigger);
    const column = columnForElement(trigger);
    if (!row || !column) return null;
    closeInlineControl();
    const restore = trigger.outerHTML;
    const holder = host.ownerDocument.createElement('span');
    holder.className = 'dashboard-inline-control';
    holder.innerHTML = build(column, row);
    trigger.replaceWith(holder);
    inlineControl = { node: holder, restore };
    // relatedTarget inside the holder is the address editor's own Custom box
    // taking focus from its select; that is still the same cell being edited.
    holder.addEventListener('focusout', (event) => {
      if (holder.contains(event.relatedTarget)) return;
      if (inlineControl?.node === holder) closeInlineControl();
    });
    return holder;
  }

  /**
   * The editor as part of the row, rather than as a panel on top of it.
   *
   * The cell's own markup is put back on the way out, so nothing here depends on
   * a repaint - and a repaint is not available anyway, because render() closes
   * editors. Commit on Enter or on leaving the box; Escape abandons.
   */
  function openInlineEditor(cell, row, column, initialValue) {
    const restore = cell.innerHTML;
    let settled = false;
    // A blank commits, except for a note (Alan, 2026-08-16: "表格的transfer address
    // 应该可以删除的为什么不能删只能改" - and, the rule: "前提是这个role有权更改这个
    // column的信息那么他就可以删除这个信息").
    //
    // This is the SIXTH gate, and it is the one that most text cells go through:
    // text, number and currency editors open in the cell rather than in the
    // popover, so the popover fix an hour earlier did not reach Transfer From,
    // SSL, PIN, Terminal, or any address. It was found by driving the real table
    // and watching what a cleared cell sent, which was nothing - after reading
    // had found five gates and stopped.
    //
    // An append-only note stays out: its box holds only the ADDITION and the
    // server refuses an empty one, so committing blank there would earn a refusal
    // every time the note was opened and left. Every other note clears like text.
    // ...and only when there was something to clear. An editor opened on an
    // already-blank cell and closed sends nothing, so no revision moves and no
    // audit line is written for a cell nobody changed.
    const blankCommits = !APPEND_ONLY_NOTE_FIELDS.has(column.key) && String(initialValue ?? '').trim() !== '';
    const finish = (save, raw) => {
      if (settled) return;
      settled = true;
      cell.innerHTML = restore;
      endCellEdit();
      if (save && (blankCommits || String(raw ?? '').trim())) void saveEditorValue(row, column, raw);
    };
    cell.innerHTML = `<span class="dashboard-inline-editor">${
      editorControlMarkup(column, initialValue, row.actual_empty_returned_precision)
    }</span>`;
    const input = cell.querySelector('[data-editor-input]');
    if (!input) {
      finish(false);
      return;
    }
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish(false);
        return;
      }
      // A note is multi-line, so Enter belongs to the text; it commits on blur.
      if (event.key !== 'Enter' || input.tagName === 'TEXTAREA') return;
      event.preventDefault();
      finish(true, input.value);
    });
    input.addEventListener('blur', () => finish(true, input.value));
    if (column.key === 'empty_container_no') {
      const editor = cell.querySelector('.dashboard-inline-editor');
      // No datalist here (Alan, 2026-09-28: "不是这种下拉选而是点击本身就有的选按钮") -
      // the browser's own suggestion list was every known empty number in one long,
      // unstyled scroll, which reads nothing like the rest of this editor. Typing
      // the number by hand still works; the "选" button below is the ONE way to
      // pick from the list instead of typing.
      // Why it was a pointer: a number typed from memory and mistyped matches
      // nothing, and the handoff silently does not happen (2026-08-03). The button
      // keeps that way in - pick the empty's own row - for when it is the safer one.
      const pick = host.ownerDocument.createElement('button');
      pick.type = 'button';
      pick.className = 'dashboard-empty-pick-button';
      pick.textContent = '选';
      pick.title = '从已报空的柜子里选 · Pick from the reported empties';
      pick.setAttribute('aria-label', pick.title);
      // mousedown rather than click: the box loses focus first on a click, and
      // blur would save whatever half a number it holds.
      pick.addEventListener('mousedown', (event) => {
        event.preventDefault();
        finish(false);
        beginEmptyPick(row);
      });
      editor?.append(pick);
    }
    input.focus();
    input.select?.();
  }

  let lastLockedHintAt = 0;

  function openEditor(trigger, row, column) {
    // Row-aware, so Chassis # cannot be opened on a Container using the customer's
    // own chassis even if the control is reached some other way (Alan, 2026-08-02).
    if (!columnEditableForRow(column, state.role, row)) return;
    if (state.locked) {
      // A locked table used to swallow the click without a word, which read as "this role
      // cannot pick a driver" (Alan, 2026-10-08: "我需要调度可以去填写司机的信息...为什么现在不可以").
      // The role can; the lock is what is closed, so say so - once, not on every tap.
      const now = Date.now();
      if (now - lastLockedHintAt > 4000) {
        lastLockedHintAt = now;
        onEditorError(new Error('表格已锁定 — 点右上角的锁解锁后再编辑 · The table is locked: unlock it (top right) to edit.'), {
          containerId: row.id, field: column.key,
        });
      }
      return;
    }
    if (column.editor === 'driver') {
      openDriverEditor(trigger, row, column);
      return;
    }
    // Empty # is no longer a pointer only (Alan, 2026-09-27: "整个流程都是直接可以
    // 手动操作的"). It opens as a text box like any other text cell; the box offers
    // the reported empties' numbers and carries a button into the pointer mode it
    // was from 2026-08-03 - see openInlineEditor.
    // Date and address cells edit in place through their own native control, so
    // they need the room but not the box - beginCellEdit is the whole change.
    if (column.editor === 'date' || column.editor === 'address') {
      beginCellEdit(row, column);
      return;
    }
    const initialValue = editorCurrentValue(row, column);
    // A text box goes in the widened cell; a menu still needs somewhere to hang
    // its options, so those keep the popover (Alan asked for the "text box" to
    // move, and a list of nine statuses is not one). Either way the column
    // widens and the row stays lit, which is what stops the misread.
    const inline = ['text', 'number', 'currency', 'note'].includes(column.editor)
      && column.key !== 'weight_lbs' && column.key !== 'size_type';
    const inlineCell = beginCellEdit(row, column);
    if (inline && inlineCell) {
      openInlineEditor(inlineCell, row, column, initialValue);
      return;
    }
    const panel = host.ownerDocument.createElement('div');
    panel.className = 'dashboard-cell-editor-popover';
    panel.innerHTML = editorControlMarkup(column, initialValue, row.actual_empty_returned_precision);
    let committed = false;
    // Whether an emptied editor is a save. It is, for everything but a note
    // (Alan, 2026-08-16: "应该可以删除的为什么不能删只能改"). Both commit paths below
    // - Enter, and clicking away - used to drop a blank on the floor, which was
    // the fourth and fifth of the gates that made a value impossible to remove:
    // the date input, the converter and the server had already been opened, and
    // this popover, which is what a text or address cell edits in, still refused.
    //
    // An append-only note is the exception and stays one: its textarea holds only
    // the ADDITION, so an empty one is nothing to add, and sending it would only
    // earn a refusal every time the note is opened and closed untouched.
    const blankCommits = !APPEND_ONLY_NOTE_FIELDS.has(column.key) && String(initialValue ?? '').trim() !== '';
    const commitWorthy = (input) => Boolean(input) && (blankCommits || String(input.value || '').trim());
    const overlay = overlayManager.open({
      anchor: trigger,
      content: panel,
      kind: column.editor === 'select' ? 'cell-options' : 'cell-editor',
      onClose(reason, closingOverlay) {
        endCellEdit();
        if (committed || !['outside', 'replace'].includes(reason)) return;
        const input = closingOverlay.querySelector?.('[data-editor-input]');
        // Untouched, the autosave controller sees the same value and does not
        // send; so committing here on every close is safe, and it is what makes
        // "select all, delete, click away" a clear rather than a no-op.
        if (commitWorthy(input)) void saveEditorValue(row, column, input.value);
      },
    });
    // Weight is stored in pounds whatever the operator typed: the unit chosen here
    // converts the entry, it does not travel with it.
    let weightUnit = 'lbs';
    const editorValueForSave = (raw) => {
      if (column.key !== 'weight_lbs') return raw;
      const pounds = poundsFromWeightInput(raw, weightUnit);
      return pounds === null ? raw : pounds;
    };
    const bindInput = (input) => {
      if (!input) return;
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || input.tagName === 'TEXTAREA') return;
        event.preventDefault();
        if (!commitWorthy(input)) return;
        committed = true;
        void saveEditorValue(row, column, editorValueForSave(input.value));
        overlayManager.close('submit');
      });
      input.focus?.();
      input.select?.();
    };
    if (column.editor === 'actual_empty_return') {
      const dateInput = overlay.querySelector?.('[data-editor-date]');
      const timeInput = overlay.querySelector?.('[data-editor-time]');
      const saveActual = () => {
        if (!String(dateInput?.value || '').trim()) return;
        committed = true;
        void saveEditorValue(row, column, { date: dateInput.value, time: timeInput?.value || '' });
        overlayManager.close('submit');
      };
      dateInput?.addEventListener('change', saveActual);
      timeInput?.addEventListener('change', saveActual);
      dateInput?.focus?.();
      return;
    }
    overlay.querySelectorAll?.('[data-editor-option]').forEach((button) => {
      button.addEventListener('click', () => {
        // Custom swaps the menu for a box instead of committing. Any column that
        // declares allowCustom gets it - size_type since 2026-08-08, ssl and
        // terminal_name since 2026-08-21 - so there is one path, not one per field.
        if (column.allowCustom && button.dataset.editorOption === CUSTOM_OPTION_VALUE) {
          panel.innerHTML = customValueMarkup(column, initialValue);
          bindInput(panel.querySelector('[data-custom-option-input]'));
          return;
        }
        committed = true;
        // Customer Paid is a value of the fee itself now, not a shared flag beside it
        // (Alan, 2026-08-02), so it saves to whichever fee's menu it was chosen from.
        // It used to be redirected into customer_paid, which set both fees at once.
        const chosen = button.dataset.editorOption;
        void saveEditorValue(row, column, chosen);
        overlayManager.close('selection');
      });
    });
    const unitButtons = overlay.querySelectorAll?.('[data-weight-unit]') ?? [];
    const hint = overlay.querySelector?.('[data-weight-hint]');
    unitButtons.forEach?.((button) => {
      button.addEventListener('click', () => {
        weightUnit = button.dataset.weightUnit === 'kg' ? 'kg' : 'lbs';
        unitButtons.forEach?.((other) => other.setAttribute(
          'aria-pressed',
          String(other.dataset.weightUnit === weightUnit),
        ));
        if (hint) {
          const typed = poundsFromWeightInput(
            overlay.querySelector?.('[data-editor-input]')?.value,
            weightUnit,
          );
          hint.textContent = weightUnit === 'kg'
            ? `Entered in kilograms; saved as ${typed === null ? 'pounds' : `${typed} lbs`}.`
            : 'Stored in pounds.';
        }
        overlay.querySelector?.('[data-editor-input]')?.focus?.();
      });
    });
    bindInput(overlay.querySelector?.('[data-editor-input]'));
  }

  function openNote(trigger, row, column) {
    const editable = !state.locked && columnEditableForRole(column, state.role);
    if (editable) {
      openEditor(trigger, row, column);
      return;
    }
    overlayManager.open({
      anchor: trigger,
      kind: 'note',
      content: `<div class="dashboard-note-popover"><strong>${escapeHtml(column.label)}</strong><p>${escapeHtml(row[column.key] || 'No note')}</p></div>`,
    });
  }

  /**
   * One row at a time, marked by a click on it and let go by any click that is not
   * (Alan, 2026-09-27: "表格里面的每一行都可以点击后锁定一个是整行这个只有暂时换颜色的效果
   * 点击除了这一行其他的那么这个颜色就会取消").
   *
   * A reading aid and nothing more: it saves nothing, and neither a reload nor a
   * click elsewhere keeps it. Every table on the page listens for itself, on the
   * whole document, so a click in the other pane of a split clears this one - and
   * in the capture phase, because the 配空 picker stops the clicks it takes.
   *
   * Not "elsewhere": the popover or menu of a cell being edited (that is this row's
   * editing), a dialog, and the table's own scrollbar - dragging along a long row
   * is exactly what the mark is for.
   */
  const ROW_MARK_IGNORED = '.dashboard-overlay-layer, dialog, .adaptive-scrollbar, .toast, [data-pairing-notice]';

  function setRowMark(id) {
    const next = typeof id === 'string' && id ? id : null;
    if (next === state.markedRowId) return;
    state.markedRowId = next;
    for (const element of host.querySelectorAll('[data-row-marked]')) delete element.dataset.rowMarked;
    if (!next) return;
    for (const element of host.querySelectorAll('[data-adaptive-row]')) {
      if (element.dataset.adaptiveRow === next) element.dataset.rowMarked = 'true';
    }
  }

  function onDocumentClickForRowMark(event) {
    if (state.destroyed) return;
    const target = event.target;
    if (!target || typeof target.closest !== 'function' || target.closest(ROW_MARK_IGNORED)) return;
    const rowElement = target.closest('[data-adaptive-row]');
    setRowMark(rowElement && host.contains(rowElement) ? rowElement.dataset.adaptiveRow : null);
  }

  function clearHighlightForRow(rowElement) {
    if (!rowElement || state.highlightedId !== rowElement.dataset.adaptiveRow) return;
    state.highlightedId = null;
    host.querySelectorAll('.is-work-queue-target').forEach((element) => {
      element.classList.remove('is-work-queue-target');
    });
  }

  async function copyValue(value) {
    if (!value) return;
    try {
      await clipboard?.writeText?.(value);
      onCopyFeedback(value);
    } catch (error) {
      onEditorError(error, { field: 'copy' });
    }
  }

  function revealLocatedRow(id) {
    const findRow = () => [...host.querySelectorAll('tr[data-adaptive-row]')]
      .find((candidate) => candidate.dataset.adaptiveRow === id);
    // A located row may be outside the rendered window. Jump the page to where
    // its spacer says it lives, repaint the window there, and it exists.
    if (!findRow()) {
      const index = windowedRows.findIndex((candidate) => candidate.id === id);
      const body = host.querySelector('.adaptive-container-table tbody');
      const view = host.ownerDocument?.defaultView;
      if (index >= 0 && body && view) {
        const top = body.getBoundingClientRect().top + view.scrollY + rowsHeightPx(windowedRows, 0, index);
        view.scrollTo({ top: Math.max(0, top - (view.innerHeight / 2)), behavior: 'auto' });
        paintRowWindow();
      }
    }
    const row = findRow();
    if (!row) return false;
    row.scrollIntoView({ block: 'center', inline: 'nearest' });
    const view = row.ownerDocument.defaultView;
    const bounds = row.getBoundingClientRect();
    if (view && (bounds.top < 0 || bounds.bottom > view.innerHeight)) {
      view.scrollTo({
        top: Math.max(0, view.scrollY + bounds.top - ((view.innerHeight - bounds.height) / 2)),
        behavior: 'auto',
      });
    }
    row.focus({ preventScroll: true });
    return true;
  }

  function locate(id) {
    if (!rowForId(id)) return false;
    state.highlightedId = id;
    render();
    return revealLocatedRow(id);
  }

  // Sorting is the locked half of the heading. Non-date headings carry no
  // data-sort-date-column, so a click on one while locked does nothing at all,
  // which is the resting behaviour Alan asked for.
  function sortByHeading(heading) {
    const key = heading?.dataset?.sortDateColumn;
    if (!key) return false;
    state.sortDateKey = nextDateSortKey(state.sortDateKey, key);
    state.sortAscending = false;
    render();
    return true;
  }

  function toggleColumnPin(columnPin) {
    // Pinning is part of editing now that the heading is the control: locked, a
    // click on a column name must do nothing (Alan, 2026-08-09).
    if (state.locked || !columnPinStore || !columnPin) return false;
    state.pinnedKeys = columnPinStore.toggle(columnPin.dataset.toggleColumnPin);
    render();
    return true;
  }

  function onPointerDown(event) {
    const columnPin = event.target.closest?.('[data-toggle-column-pin]');
    if (!columnPin || event.button !== 0) return;
    if (toggleColumnPin(columnPin)) event.preventDefault();
  }

  function columnForElement(element) {
    const field = element?.dataset?.field
      || element?.closest?.('[data-field]')?.dataset?.field
      || '';
    return field ? allColumns().find(({ key }) => key === field) || null : null;
  }

  function rowForElement(element) {
    const rowElement = element?.closest?.('[data-adaptive-row]');
    return rowElement ? rowForId(rowElement.dataset.adaptiveRow) : null;
  }

  function onChange(event) {
    const target = event.target;
    if (!target?.matches) return;
    const row = rowForElement(target);
    const column = columnForElement(target);
    if (!row || !column) return;
    if (target.matches('[data-date-input]')) {
      // Blank saves too. It used to be dropped here - the first of three gates
      // that between them made a date impossible to remove (Alan, 2026-08-16),
      // and this one silently: the picker cleared, nothing was sent, and the old
      // date came back on the next repaint.
      void saveEditorValue(row, column, target.value);
      return;
    }
    if (target.matches('[data-address-select]')) {
      const custom = target.closest('[data-address-editor]')?.querySelector('[data-address-custom]');
      if (target.value === CUSTOM_ADDRESS_VALUE) {
        if (custom) {
          custom.hidden = false;
          custom.focus?.();
          custom.select?.();
        }
        return;
      }
      if (custom) custom.hidden = true;
      void saveEditorValue(row, column, target.value);
      return;
    }
    if (target.matches('[data-address-custom]')) void saveEditorValue(row, column, target.value);
  }

  function onClick(event) {
    if (event.target.closest?.('[data-cancel-empty-pick]')) {
      endEmptyPick();
      return;
    }
    if (externalPick) {
      // Any row here is an empty the other table may take; a click on one hands its number
      // back and the mode ends. Anything else is ignored, so a stray click opens nothing.
      const candidate = event.target.closest?.('[data-adaptive-row]');
      const picked = candidate ? rowForId(candidate.dataset.adaptiveRow) : null;
      if (picked && containerNumberOf(picked)) {
        const pending = externalPick;
        externalPick = null;
        render();
        pending.onPick(picked);
      }
      return;
    }
    if (emptyPick.isActive()) {
      // While picking, a row click means "this is the empty", not "open this row".
      const candidate = event.target.closest?.('[data-adaptive-row]');
      const picked = candidate ? rowForId(candidate.dataset.adaptiveRow) : null;
      if (picked && completeEmptyPick(picked)) return;
      return;
    }
    // A date cell becomes the native control on the click that opens it, so the
    // calendar still appears on one click - it is just built at that moment
    // rather than sitting in all 452 rows waiting to be used.
    const dateTrigger = state.locked ? null : event.target.closest?.('[data-open-date-input]');
    if (dateTrigger) {
      const input = openInlineControl(dateTrigger, (column, row) => dateInputMarkup({ column, row }))
        ?.querySelector('[data-date-input]');
      input?.focus?.();
      // Pickup Appt is typed (2026-09-26): the box opens selected, ready to overwrite.
      if (input?.type === 'text') input.select?.();
      else try { input?.showPicker?.(); } catch (_) { /* unsupported */ }
      return;
    }
    const addressTrigger = state.locked ? null : event.target.closest?.('[data-open-address-editor]');
    if (addressTrigger) {
      const select = openInlineControl(addressTrigger, (column, row) => addressEditorMarkup({
        column, row, catalog: resolveAddressCatalog(), locale: currentLocale(),
      }))?.querySelector('[data-address-select], [data-address-custom]');
      select?.focus?.();
      try { select?.showPicker?.(); } catch (_) { /* unsupported */ }
      return;
    }
    const dateInput = event.target.closest?.('[data-date-input]');
    if (dateInput) {
      if (dateInput.type !== 'text') {
        try { dateInput.showPicker?.(); } catch (_) { /* already open or unsupported */ }
      }
      return;
    }
    // One heading, two actions, chosen by the lock rather than by the markup:
    // locked it sorts, unlocked it pins. The two never collide because pinning
    // is an edit and sorting is reading (Alan, 2026-08-10).
    const heading = event.target.closest('[data-sort-date-column], [data-toggle-column-pin]');
    if (heading) {
      // Unlocked, pointer activation was already committed on pointerdown
      // because live table data can replace a header node before pointerup.
      // detail === 0 preserves keyboard-generated clicks without toggling twice.
      if (state.locked) sortByHeading(heading);
      else if (event.detail === 0) toggleColumnPin(heading);
      return;
    }
    const rowElement = event.target.closest('[data-adaptive-row]');
    clearHighlightForRow(rowElement);
    const row = rowElement ? rowForId(rowElement.dataset.adaptiveRow) : null;
    const inspectorButton = event.target.closest('[data-open-inspector]');
    if (inspectorButton && row) {
      onInspectRow(row, inspectorButton);
      return;
    }
    // Copy is the locked behaviour of an identifier. A cell that is also an
    // editor trigger copies only while locked; unlocked, the click belongs to
    // the editor further down. A plain copy target - a column this role cannot
    // edit - copies in both states, as it always has.
    const copyButton = event.target.closest('[data-copy-value]');
    if (copyButton && (state.locked || !copyButton.hasAttribute('data-open-cell-editor'))) {
      void copyValue(copyButton.dataset.copyValue);
      return;
    }
    const podButton = event.target.closest('[data-open-pod]');
    if (podButton && row) {
      onOpenPod(row, podButton);
      return;
    }
    const podEditorButton = event.target.closest('[data-open-pod-editor]');
    if (podEditorButton && row) {
      onOpenPodEditor(row, podEditorButton);
      return;
    }
    const noteButton = event.target.closest('[data-open-note]');
    if (noteButton && row) {
      const column = allColumns().find(({ key }) => key === noteButton.dataset.field);
      if (column) openNote(noteButton, row, column);
      return;
    }
    // The address map popup is gone (Alan, 2026-08-10). An address cell shows its
    // short form, reveals the whole thing on hover, and - when the table is
    // unlocked - opens the ordinary cell editor through data-open-cell-editor
    // below, which is what keeps the three text address columns editable now
    // that the map's onUpdate is no longer there to save them.
    const reportEmptyButton = state.locked ? null : event.target.closest('[data-report-empty]');
    if (reportEmptyButton && row) {
      // Staff say where the empty is as they report it; a customer's press is still
      // the single tap it always was.
      if (reportEmptyButton.hasAttribute('data-report-empty-staff')) {
        openReportEmptyEntry(reportEmptyButton, row);
        return;
      }
      reportEmptyButton.disabled = true;
      void saveReportedEmpty(row, new Date().toISOString())
        .then((saved) => { if (!saved) reportEmptyButton.disabled = false; });
      return;
    }
    const cancelReportEmpty = state.locked ? null : event.target.closest('[data-cancel-report-empty]');
    if (cancelReportEmpty && row) {
      openCancelReportEmpty(cancelReportEmpty, row);
      return;
    }
    const editorButton = event.target.closest('[data-open-cell-editor]');
    if (editorButton && row) {
      const column = allColumns().find(({ key }) => key === editorButton.dataset.field)
        || syntheticEditorColumn(editorButton.dataset.field);
      if (column) openEditor(editorButton, row, column);
      return;
    }
    const selectionButton = event.target.closest('[data-select-row]');
    if (selectionButton && row) {
      navigate(`#/container/${encodeURIComponent(row.id)}`);
      return;
    }
    const link = event.target.closest('a[href^="#/"]');
    if (link) {
      event.preventDefault();
      navigate(link.getAttribute('href'));
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && endEmptyPick()) {
      event.preventDefault();
      return;
    }
    // A typed date commits on Enter the way the picker commits on a click, and
    // Escape puts back what was there - blur is what closes the cell either way.
    const typedDate = event.target.closest?.('input[data-date-input][type="text"]');
    if (typedDate && (event.key === 'Enter' || event.key === 'Escape')) {
      event.preventDefault();
      if (event.key === 'Escape') typedDate.value = typedDate.dataset.original ?? '';
      typedDate.blur();
      return;
    }
    if (!['Enter', ' '].includes(event.key)) return;
    const heading = event.target.closest?.('[data-sort-date-column], [data-toggle-column-pin]');
    if (heading) {
      event.preventDefault();
      if (state.locked) sortByHeading(heading);
      else toggleColumnPin(heading);
      return;
    }
    const row = event.target.closest('[data-adaptive-row]');
    if (!row || event.target !== row) return;
    event.preventDefault();
    clearHighlightForRow(row);
  }

  /**
   * Scrolling repaints the row window and nothing else.
   *
   * Straight off the scroll event, not through requestAnimationFrame. A frame
   * callback runs after the scroll has already been composited, so the rows
   * always arrived one frame behind the spacers - which is a band of blank paper
   * on every flick. The browser already coalesces scroll to one event per frame,
   * and the repaint is incremental, so doing it here lands the rows in the same
   * frame that moved them.
   */
  function mountRowWindowScroll() {
    const view = host.ownerDocument?.defaultView;
    if (!view?.addEventListener) return;
    const onScroll = () => paintRowWindow();
    view.addEventListener('scroll', onScroll, { passive: true });
    view.addEventListener('resize', onScroll, { passive: true });
    host.ownerDocument?.addEventListener('scroll', onScroll, { capture: true, passive: true });
    rowWindowCleanup = () => {
      view.removeEventListener('scroll', onScroll);
      view.removeEventListener('resize', onScroll);
      host.ownerDocument?.removeEventListener('scroll', onScroll, true);
    };
  }

  // Capture phase, so a highlight click is taken before the cell's own handlers (editor,
  // copy, note) ever see it.
  function onHighlightClick(event) {
    if (!state.highlightColor) return;
    const cell = event.target.closest?.('td[data-column-key]');
    const rowElement = cell?.closest?.('[data-adaptive-row]');
    if (!cell || !rowElement) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const key = `${rowElement.dataset.adaptiveRow}|${cell.dataset.columnKey}`;
    if (state.cellMarks.get(key) === state.highlightColor) {
      state.cellMarks.delete(key);
      cell.removeAttribute('data-cell-mark');
    } else {
      state.cellMarks.set(key, state.highlightColor);
      cell.setAttribute('data-cell-mark', state.highlightColor);
    }
  }

  host.addEventListener('click', onHighlightClick, true);
  host.addEventListener('click', onSelectBoxClick, true);
  host.addEventListener('pointerdown', onPointerDown);
  host.addEventListener('click', onClick);
  host.ownerDocument?.addEventListener('click', onDocumentClickForRowMark, true);
  host.addEventListener('change', onChange);
  host.addEventListener('keydown', onKeydown);
  mountRowWindowScroll();
  render();
  return Object.freeze({
    setRows(next) {
      // The data controller reports twice per page fetch - once on entry with
      // loading=true and the rows unchanged, once on success - and this used to
      // rebuild the whole table both times. Rebuilding for data that did not
      // change is the single cheapest render to skip, so an identical row set
      // by identity and revision is dropped here.
      if (rowsAreIdentical(state.rows, next)) return;
      state.rows = [...next];
      if (!next.some(({ id }) => id === state.highlightedId)) state.highlightedId = null;
      if (overlayManager.isOpen()) {
        pendingRender = true;
        return;
          }
          render();
          if (state.highlightedId) revealLocatedRow(state.highlightedId);
        },
    /**
     * Mark the cells on one row that another Container is about to write into.
     *
     * The values themselves arrive through setRows - the caller patches the row
     * with what the pairing will write - so this only says WHICH cells came from
     * elsewhere. Two calls, one repaint each, and the table does not move: the
     * operator is mid-decision.
     */
    setPairingPreview({ rowId = '', fields = null } = {}) {
      const keys = Array.isArray(fields) ? fields.filter(Boolean) : [];
      state.pairingPreview = keys.length && rowId
        ? { rowId: String(rowId), containerNo: '', fields: new Set(keys) }
        : { rowId: '', containerNo: '', fields: null };
      repaintPreviewCells();
    },
    /**
     * Sort by a date column from outside the table.
     *
     * The heading has always been able to do this; nothing else could, so a chip
     * that means "these, in this order" had no way to say the second half (Alan,
     * 2026-08-16: the Loaded chip sorts by pickup appointment). Null puts the
     * resting order back - late first, then owed, then done - which is what every
     * other chip wants.
     *
     * Same direction as a heading click, newest first, deliberately: two ways to
     * reach one sort that disagreed about which end it starts from would be worse
     * than either.
     */
    setDateSort(key, { ascending = false } = {}) {
      const next = typeof key === 'string' && key ? key : null;
      const direction = Boolean(next) && ascending === true;
      if (state.sortDateKey === next && state.sortAscending === direction) return state.sortDateKey;
      state.sortDateKey = next;
      state.sortAscending = direction;
      render();
      return state.sortDateKey;
    },
    setActiveDispatchIds(ids) {
      state.activeDispatchIds = Array.isArray(ids) ? new Set(ids) : null;
      render();
      return state.activeDispatchIds;
    },
    setRowIdFilter(ids) {
      state.rowIdFilter = Array.isArray(ids) ? new Set(ids) : null;
      if (state.highlightedId && !filteredTableRows()
        .some(({ id }) => id === state.highlightedId)) {
        state.highlightedId = null;
      }
      render();
    },
    /**
     * Marks the rows the pairing flow has picked. Repainting the two rows in
     * place rather than re-rendering: the operator is mid-choice and the table
     * must not move under them.
     */
    setPairingSelection({ ladenId = '', emptyId = '' } = {}) {
      state.pairingPick = { ladenId: String(ladenId || ''), emptyId: String(emptyId || '') };
      // The Empty # preview when both Containers are in THIS table, which is the
      // single-table case. In the split they are in two, so the caller drives the
      // marking with setPairingPreview instead and this leaves it alone.
      const emptyRow = state.pairingPick.emptyId
        ? state.rows.find(({ id }) => id === state.pairingPick.emptyId) : null;
      if (emptyRow || !state.pairingPreview.fields) {
        state.pairingPreview = state.pairingPick.ladenId && emptyRow
          ? {
            rowId: state.pairingPick.ladenId,
            containerNo: String(emptyRow.container_no ?? '').trim(),
            fields: new Set(['empty_container_no']),
          }
          : { rowId: '', containerNo: '', fields: null };
      }
      repaintPreviewCells();
      for (const element of host.querySelectorAll('[data-adaptive-row]')) {
        const id = element.dataset.adaptiveRow;
        const pick = id === state.pairingPick.ladenId ? 'laden'
          : id === state.pairingPick.emptyId ? 'empty' : '';
        if (pick) {
          element.dataset.pairingPick = pick;
          element.setAttribute('aria-selected', 'true');
        } else {
          delete element.dataset.pairingPick;
          element.removeAttribute('aria-selected');
        }
      }
    },
    /**
     * Marks the Containers whose numbers just went to the clipboard.
     *
     * Copying a group is the first half of a lookup - you paste it into the
     * terminal's site and go and read the answer - so the rows you took have to
     * be visible when you come back, and they accumulate across groups so you can
     * see how far through the fleet you are (Alan, 2026-08-09). Stamped on the
     * live rows rather than re-rendered, and kept in state so rows scrolled back
     * into the window come back marked.
     */
    markCopiedContainers(numbers) {
      // Re-read first: another pane may have marked or cleared since this one was drawn.
      readCopiedMarks();
      for (const number of Array.isArray(numbers) ? numbers : []) {
        const key = normalizedCopyKey(number);
        if (key) state.copiedNumbers.add(key);
      }
      saveCopiedMarks();
      syncCopiedMarks();
      return state.copiedNumbers.size;
    },
    /** Select mode: a box on every row. Leaving it forgets the ticks. */
    setSelectMode(on) {
      const next = Boolean(on);
      if (next === state.selectMode) return state.selectMode;
      state.selectMode = next;
      if (!next) {
        state.selectedIds.clear();
        state.lastSelectedId = null;
      }
      host.classList.toggle('is-selecting', next);
      render();
      announceSelection();
      return state.selectMode;
    },
    isSelectMode() {
      return state.selectMode;
    },
    selectedRows() {
      return selectedVisibleRows();
    },
    selectionCount() {
      return selectedVisibleRows().length;
    },
    visibleRowCount() {
      return windowedRows.length;
    },
    /** Tick every row the current filter shows. */
    selectAllVisible() {
      if (!state.selectMode) return 0;
      windowedRows.forEach((row) => state.selectedIds.add(row.id));
      paintSelection();
      return selectedVisibleRows().length;
    },
    clearSelection() {
      state.selectedIds.clear();
      state.lastSelectedId = null;
      paintSelection();
    },
    /** Pick the highlighter's colour, or null to put it down. The marks it left stay. */
    setHighlightColor(color) {
      state.highlightColor = HIGHLIGHT_COLORS.includes(color) ? color : null;
      host.classList.toggle('is-highlighting', state.highlightColor !== null);
    },
    highlightColor() {
      return state.highlightColor;
    },
    cellMarkCount() {
      return state.cellMarks.size;
    },
    clearCellMarks() {
      state.cellMarks.clear();
      host.querySelectorAll('[data-cell-mark]').forEach((cell) => cell.removeAttribute('data-cell-mark'));
    },
    clearCopiedContainers() {
      state.copiedNumbers.clear();
      state.previewNumbers.clear();
      saveCopiedMarks();
      syncCopiedMarks();
    },
    /** Highlight the group the copy panel is on. Replaces the previous one. */
    previewCopyGroup(numbers) {
      state.previewNumbers.clear();
      for (const number of Array.isArray(numbers) ? numbers : []) {
        const key = normalizedCopyKey(number);
        if (key) state.previewNumbers.add(key);
      }
      syncCopiedMarks();
      return state.previewNumbers.size;
    },
    copiedContainerCount() {
      readCopiedMarks();
      return state.copiedNumbers.size;
    },
    /**
     * The rows in the order they are drawn right now - the resting Ocean Status
     * order, or the date a heading was clicked to sort by - with the same filter.
     * Bulk paste lays "table order" against this, so it has to be the order the
     * operator is looking at, not the order the rows arrived in.
     */
    rowsInDisplayOrder() {
      return [...windowedRows];
    },
    /**
     * Be pointed at for another table: its Empty # cell asked to pick, and this is where
     * the empties are. `onPick(row)` is called with the row clicked, `onCancel()` if the
     * operator gives up (Cancel, Escape). False when there is nothing here to point at.
     */
    beginExternalEmptyPick({ onPick, onCancel } = {}) {
      if (typeof onPick !== 'function' || !state.rows.length) return false;
      externalPick = { onPick, onCancel };
      render();
      return true;
    },
    /**
     * The lock. Instant on purpose: the cells are already built as their editable
     * selves, so this flips one class and one flag and repaints nothing. Turning
     * the old Edit mode on rewrote thirteen thousand cells, which is what made it
     * feel broken.
     */
    /** The searched numbers, in the order typed; the rows follow it until a date column is sorted. */
    setSearchOrder(terms) {
      const next = Array.isArray(terms) ? terms.map((term) => String(term ?? '').trim().toUpperCase()).filter(Boolean) : [];
      if (JSON.stringify(next) === JSON.stringify(state.searchOrder)) return;
      state.searchOrder = next;
      render();
    },
    setEmptyReason(reason) {
      const next = String(reason ?? '').trim();
      if (next === state.emptyReason) return;
      state.emptyReason = next;
      // Only worth a repaint when the empty row is what is on screen.
      if (!state.rows.length) render();
    },
    setLocked(locked) {
      state.locked = locked !== false;
      host.classList.toggle('is-locked', state.locked);
      return state.locked;
    },
    isLocked() {
      return state.locked;
    },
    setLocale() { render(); },
    select: locate,
    locate,
    destroy() {
      state.destroyed = true;

      nearEndObserver?.disconnect();
      nearEndObserver = null;
      nearEndScrollCleanup();
      nearEndScrollCleanup = () => {};
      rowWindowCleanup();
      rowWindowCleanup = () => {};
      overlayManager.destroy();
      cleanupScrollbars();
      tierController.destroy();
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('click', onClick);
      host.removeEventListener('click', onHighlightClick, true);
      host.removeEventListener('click', onSelectBoxClick, true);
      host.ownerDocument?.removeEventListener('click', onDocumentClickForRowMark, true);
      host.removeEventListener('change', onChange);
      host.removeEventListener('keydown', onKeydown);
    },
  });
}
